/**
 * Board Calculator — Custom Element
 * <board-calculator> Web Component for mifapladen.dk
 */
if (!customElements.get('board-calculator')) {
  customElements.define('board-calculator', class BoardCalculator extends HTMLElement {

    // P3 #22: Constants — no more magic numbers
    static CUTOUT_Y_MM = 60;           // Fixed 6cm from front edge A
    static CLEARANCE_AC_MM = 65;       // Min clearance from A/C edges
    static CLEARANCE_BD_MM = 150;      // Min clearance from B/D edges
    static ASSEMBLY_FEE_DEFAULT = 716; // DKK per joint
    static CORNER_PRICE_DEFAULT = 120; // DKK per corner
    static VAT_RATE_DEFAULT = 0.25;
    static MIN_LENGTH_DEFAULT = 300;   // mm
    static MIN_WIDTH_DEFAULT = 100;    // mm
    static PNG_SCALE = 2;             // Canvas scale multiplier for PNG export
    // Aug 2026 stone phase: step 2 (main material) was inserted, taking the flow from 6
    // steps to 7. Kept as a constant because the old code hardcoded "6" in five places
    // and every one of them had to be found by hand.
    static TOTAL_STEPS = 7;
    static DEFAULT_MAIN_MATERIAL = 'wood';

    constructor() {
      super();

      // P3 #21: Config loading with error handling
      const configEl = this.querySelector('#calculator-config');
      try {
        this.config = configEl ? JSON.parse(configEl.textContent) : {};
      } catch (e) {
        console.warn('BoardCalculator: Failed to parse config JSON', e);
        this.config = {};
      }

      // Validate required config sections
      if (!this.config.materials?.length) console.warn('BoardCalculator: No materials configured');
      if (!this.config.edges?.length) console.warn('BoardCalculator: No edges configured');
      if (!this.config.surfaces?.length) console.warn('BoardCalculator: No surfaces configured');

      // State
      this.state = this.getDefaultState();

      this.formationBoardCounts = {
        single_rect: 1, round: 1,
        l_right: 2, l_left: 2, l_top: 2, l_bottom: 2,
        u_sides: 3, u_top_bottom: 3
      };

      // P5 (23 April 2026 feedback): per-formation per-board ABCD label map.
      // Each value picks WHICH SIDE of the SVG rect (top/right/bottom/left) gets the letter.
      // Rule: A = front of the length side (length = grain direction), then clockwise B, C, D.
      // Horizontal boards default to A=bottom (front faces user), vertical boards use the side
      // facing INTO the open area / interior of the formation.
      // Geometry below mirrors the formation icons in calculator-step-formation.liquid.
      this.formationLabels = {
        single_rect: {
          1: { bottom: 'A', left: 'B', top: 'C', right: 'D' }
        },
        round: { 1: {} },
        // l_right: board 1 = vertical bottom-LEFT leg, board 2 = horizontal full TOP
        l_right: {
          1: { right: 'A', bottom: 'B', left: 'C', top: 'D' },
          2: { bottom: 'A', left: 'B', top: 'C', right: 'D' }
        },
        // l_left: board 1 = vertical full-LEFT (tall), board 2 = horizontal top-RIGHT
        l_left: {
          1: { right: 'A', bottom: 'B', left: 'C', top: 'D' },
          2: { bottom: 'A', left: 'B', top: 'C', right: 'D' }
        },
        // l_top: board 1 = horizontal full TOP, board 2 = vertical bottom-RIGHT
        l_top: {
          1: { bottom: 'A', left: 'B', top: 'C', right: 'D' },
          2: { left: 'A', top: 'B', right: 'C', bottom: 'D' }
        },
        // l_bottom (Round 6, 5 May): board 1 = horizontal TOP-LEFT (partial width),
        //                              board 2 = vertical FULL-RIGHT (taller than board 1).
        // Replaces the previous geometry which was visually identical to l_right (a
        // horizontal-top + bottom-left-leg L) — Thomas circled it as the duplicate.
        l_bottom: {
          1: { bottom: 'A', left: 'B', top: 'C', right: 'D' },
          2: { left: 'A', top: 'B', right: 'C', bottom: 'D' }
        },
        // u_sides: board 1 = vertical bottom-LEFT, board 2 = horizontal full TOP, board 3 = vertical bottom-RIGHT
        u_sides: {
          1: { right: 'A', bottom: 'B', left: 'C', top: 'D' },
          2: { bottom: 'A', left: 'B', top: 'C', right: 'D' },
          3: { left: 'A', top: 'B', right: 'C', bottom: 'D' }
        },
        // u_top_bottom: board 1 = vertical full-LEFT, board 2 = horizontal top-CENTER, board 3 = vertical full-RIGHT
        u_top_bottom: {
          1: { right: 'A', bottom: 'B', left: 'C', top: 'D' },
          2: { bottom: 'A', left: 'B', top: 'C', right: 'D' },
          3: { left: 'A', top: 'B', right: 'C', bottom: 'D' }
        }
      };

      // P1 #12: Valid corners per formation — only corners where two outer edges meet.
      // Corner key 'AB' means the corner where sides A and B meet. Re-derived from formationLabels:
      // for each board we list corners that don't sit at a board-to-board joint.
      this.formationCorners = {
        single_rect: { 1: ['AB', 'BC', 'CD', 'DA'] },
        round: {},
        // l_right b1 vertical bottom-left (joins board 2 at top): top sides D and C are joined → free corners use bottom (B) sides
        l_right:  { 1: ['AB', 'BC'], 2: ['BC', 'CD', 'DA'] },
        l_left:   { 1: ['AB', 'BC'], 2: ['BC', 'CD', 'DA'] },
        l_top:    { 1: ['AB', 'BC', 'CD'], 2: ['AB', 'BC'] },
        // l_bottom (Round 6): board 1 horizontal top-LEFT — right edge fully joined to board 2,
        //   so only AB (bottom-left) and BC (top-left) are exposed.
        // board 2 vertical full-RIGHT — only top-left (AB) is joined; BC (top-right), CD (bottom-right), DA (bottom-left) are free.
        l_bottom: { 1: ['AB', 'BC'], 2: ['BC', 'CD', 'DA'] },
        u_sides:  { 1: ['AB', 'BC'], 2: ['BC', 'CD'], 3: ['CD', 'DA'] },
        u_top_bottom: { 1: ['AB', 'BC'], 2: ['AB', 'BC'], 3: ['CD', 'DA'] }
      };

      this.activeCorner = null; // P5 #2: highlighted corner key (e.g. '1_AB') while user is editing

      this.init();
    }

    init() {
      this.bindNavigation();
      this.bindFormation();
      this.bindUnits();
      this.bindThickness();
      this.bindDimensions();
      this.bindCutouts();
      this.bindSurface();
      this.bindDownload();
      this.bindReset();
      this.renderMainMaterials();
      this.renderMaterials();
      this.renderPackages();
      this.renderSurfaceOptions();
      this.initDefaultStates();
      this.bindSinkPicker(); // P5 #4 (23 April)
      this.updatePrice();
    }

    /** P5 #4 (23 April): fetch sinks from /collections/kokkenvaske once and
     *  populate the picker. When the user picks one, store the variant_id so
     *  addToCart() can add it as a second line item.
     *
     *  Followup #2 (24 June 2026): the native <select data-sink-product> was
     *  replaced with a custom popover picker that shows image thumbnails per
     *  sink. bindNewSinkPicker resolves the new structure via [data-sink-picker]
     *  and is the single source of truth for sink selection. It guards on the
     *  element being present so pages that don't render the surface step
     *  (none today, but defensive) don't crash the calculator boot.
     */
    bindSinkPicker() {
      this.bindNewSinkPicker();
    }

    bindNewSinkPicker() {
      const picker = this.querySelector('[data-sink-picker]');
      if (!picker) return;
      this.sinkPicker = {
        root: picker,
        trigger: picker.querySelector('[data-sink-trigger]'),
        triggerThumb: picker.querySelector('[data-sink-trigger-thumb]'),
        triggerPlaceholder: picker.querySelector('[data-sink-trigger-placeholder]'),
        triggerTitle: picker.querySelector('[data-sink-trigger-title]'),
        triggerPrice: picker.querySelector('[data-sink-trigger-price]'),
        panel: picker.querySelector('[data-sink-panel]'),
        search: picker.querySelector('[data-sink-search]'),
        list: picker.querySelector('[data-sink-list]')
      };
      this.sinkOptions = [];
      // i18n strings come from the picker's data attributes, populated by the
      // Liquid snippet from the `calculator.surface.choose_sink_*` translation
      // keys. Guard against Shopify's "Translation missing: …" sentinel —
      // it can appear briefly after a locale push while the edge cache is
      // still serving the old translation file. Falling through to a Danish
      // hardcoded default keeps the picker readable in every case.
      const validI18n = (s) => s && !/^Translation missing/.test(s) ? s : null;
      this.sinkPlaceholderText = validI18n(picker.dataset.sinkPlaceholder) || 'Vælg en vask…';
      this.sinkNoMatchesText = validI18n(picker.dataset.sinkNoMatches) || 'Ingen vaske matchede';
      // Pre-fill the trigger title with the placeholder text — products fetch
      // quickly enough that the "Henter vaske…" intermediate is rarely seen.
      this.sinkPicker.triggerTitle.textContent = this.sinkPlaceholderText;
      // Scrub the search-input placeholder too: Shopify's edge cache for the
      // locale file can lag a few minutes behind a push, briefly showing
      // "Translation missing: …" inside the placeholder. Replace with a
      // sensible Danish default in that case.
      const searchPh = this.sinkPicker.search.getAttribute('placeholder') || '';
      if (!validI18n(searchPh)) this.sinkPicker.search.setAttribute('placeholder', 'Søg vask…');

      this.loadSinkProducts();
      // Round 6 follow-up (7 May 2026): the Liquid-injected `config.sinks`
      // baseline covers only the first 50 products in `kokkenvaske` (Shopify's
      // hard cap on `collection.products`). Fetch the full metafield map from
      // the Netlify backend and merge — silent fallback to the baseline if the
      // request fails.
      this.loadSinkMetafields();

      this.sinkPicker.trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleSinkPanel();
      });

      this.sinkPicker.search.addEventListener('input', () => {
        this.renderSinkList(this.sinkPicker.search.value);
      });
      // Keep clicks/keys inside the panel from bubbling to the outside-click
      // handler (which would close the panel on every keystroke).
      this.sinkPicker.panel.addEventListener('click', (e) => e.stopPropagation());

      this.sinkPicker.list.addEventListener('click', (e) => {
        const li = e.target.closest('[data-sink-handle]');
        if (li) this.selectSink(li.dataset.sinkHandle);
      });

      // Close on outside click
      this._sinkOutsideClick = (e) => {
        if (!picker.contains(e.target)) this.closeSinkPanel();
      };
      document.addEventListener('click', this._sinkOutsideClick);

      // Escape closes; arrow keys move focus through the list when the panel is open
      picker.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && picker.hasAttribute('data-open')) {
          this.closeSinkPanel();
          this.sinkPicker.trigger.focus();
        }
      });
    }

    /** P6 #5 (5 May): when a sink is picked, auto-fill cutout width/depth from
     *  the sink product's `custom.vask_udskaering_laengde` (length, mm) and
     *  `custom.vask_udskaering_dybde` (depth, mm) metafields, injected into
     *  this.config.sinks server-side via Liquid in sections/calculator.liquid.
     *  If either metafield is absent, leaves the inputs untouched (manual entry
     *  remains the fallback path). */
    applySinkMetafields(handle) {
      const meta = (this.config.sinks || []).find(s => s.handle === handle);
      if (!meta) return;
      const lenMm = parseFloat(meta.udskaering_laengde_mm);
      const depMm = parseFloat(meta.udskaering_dybde_mm);
      if (!lenMm || !depMm) return;
      const factor = this.state.unit === 'cm' ? 10 : 1;  // mm → user unit
      const widthVal = (lenMm / factor).toString().replace('.', ',');
      const depthVal = (depMm / factor).toString().replace('.', ',');
      const sink = this.state.cutouts.sink;
      sink.width = widthVal;
      sink.depth = depthVal;
      const wInput = this.querySelector('[data-cutout="sink"][data-dim="width"]');
      const dInput = this.querySelector('[data-cutout="sink"][data-dim="depth"]');
      if (wInput) wInput.value = widthVal;
      if (dInput) dInput.value = depthVal;
      this.updateSVG();
      this.validateCutouts();
    }

    async loadSinkMetafields() {
      try {
        const res = await fetch('https://mifa-backend.netlify.app/.netlify/functions/get-sink-metafields');
        if (!res.ok) { this.log('loadSinkMetafields non-2xx', res.status); return; }
        const data = await res.json();
        const incoming = Array.isArray(data.sinks) ? data.sinks : [];
        if (!incoming.length) return;
        // Merge: Netlify entries override the Liquid baseline by handle.
        const baseline = Array.isArray(this.config.sinks) ? this.config.sinks : [];
        const byHandle = new Map(baseline.map(s => [s.handle, s]));
        incoming.forEach(s => byHandle.set(s.handle, s));
        this.config.sinks = [...byHandle.values()];
        this.log('loadSinkMetafields merged', this.config.sinks.length);
      } catch (e) {
        this.log('loadSinkMetafields failed', e?.message || e);
      }
    }

    async loadSinkProducts() {
      const list = this.sinkPicker?.list;
      if (!list) return;
      try {
        // Aug 2026: the collection depends on the main material. Stone boards take a
        // different sink range (49 products in `vaske-til-stenbordplader`) from wood's 95.
        const collection = this.getMainMaterial().sink_collection || 'kokkenvaske';
        this.loadedSinkCollection = collection;
        const res = await fetch(`/collections/${encodeURIComponent(collection)}/products.json?limit=250`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const products = data.products || [];
        if (!products.length) {
          this.sinkOptions = [];
          list.innerHTML = '<li class="calculator__sink-picker__empty">Ingen vaske fundet</li>';
          return;
        }
        // Sort alphabetically by title (Danish collation)
        products.sort((a, b) => a.title.localeCompare(b.title, 'da'));
        // Followup #2: extract featured_image so the new picker can render
        // thumbnails. Shopify's products.json returns either `featured_image`
        // (protocol-less CDN URL) or `images: [{src}]`; fall back through both
        // and finally an empty string (the picker shows a generic sink icon
        // when image is empty).
        this.sinkOptions = products
          .filter(p => p.variants?.[0])
          .map(p => {
            const v = p.variants[0];
            const price = parseFloat(v.price) || 0;
            let image = p.featured_image || '';
            if (!image && Array.isArray(p.images) && p.images.length) {
              const first = p.images[0];
              image = typeof first === 'string' ? first : (first?.src || '');
            }
            return {
              handle: p.handle,
              title: p.title,
              variant_id: v.id,
              price,
              priceLabel: price.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr.',
              image
            };
          });
        this.renderSinkList('');
        // If something is already selected (e.g. coming back from a previous
        // step), reflect it in the trigger now that we have the option data.
        const handle = this.state.cutouts.sink.product_handle;
        if (handle) this.updateSinkTrigger(this.sinkOptions.find(s => s.handle === handle));
      } catch (err) {
        console.warn('[BoardCalculator] Failed to load sinks:', err);
        list.innerHTML = '<li class="calculator__sink-picker__empty">Kunne ikke hente vaske</li>';
      }
    }

    /** Render the option list into [data-sink-list], optionally filtered by a
     *  case-insensitive substring match against the title. The aria-selected
     *  attribute mirrors the currently-chosen sink for keyboard / SR users. */
    renderSinkList(filter) {
      if (!this.sinkPicker?.list) return;
      const selected = this.state.cutouts.sink.product_handle;
      const needle = (filter || '').trim().toLowerCase();
      const options = needle
        ? this.sinkOptions.filter(s => s.title.toLowerCase().includes(needle))
        : this.sinkOptions;
      if (!options.length) {
        this.sinkPicker.list.innerHTML = `<li class="calculator__sink-picker__empty">${this.escapeAttr(this.sinkNoMatchesText)}</li>`;
        return;
      }
      this.sinkPicker.list.innerHTML = options.map(s => `
        <li
          class="calculator__sink-picker__item"
          role="option"
          tabindex="-1"
          data-sink-handle="${this.escapeAttr(s.handle)}"
          aria-selected="${s.handle === selected ? 'true' : 'false'}"
        >
          <span class="calculator__sink-picker__item__thumb-wrap">
            ${s.image
              ? `<img class="calculator__sink-picker__item__thumb" src="${this.escapeAttr(s.image)}" alt="" loading="lazy" width="40" height="40">`
              : `<svg class="calculator__sink-picker__item__thumb-placeholder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`
            }
          </span>
          <span class="calculator__sink-picker__item__text">
            <span class="calculator__sink-picker__item__title">${this.escapeAttr(s.title)}</span>
            <span class="calculator__sink-picker__item__price">${this.escapeAttr(s.priceLabel)}</span>
          </span>
        </li>
      `).join('');
    }

    /** Update the trigger button to reflect a chosen sink (or clear it when
     *  passed null). Keeps the placeholder text shown when nothing is picked. */
    updateSinkTrigger(option) {
      if (!this.sinkPicker) return;
      const { triggerThumb, triggerPlaceholder, triggerTitle, triggerPrice } = this.sinkPicker;
      if (option) {
        triggerTitle.textContent = option.title;
        triggerPrice.textContent = option.priceLabel;
        triggerPrice.hidden = false;
        if (option.image) {
          triggerThumb.src = option.image;
          triggerThumb.alt = option.title;
          triggerThumb.hidden = false;
          triggerPlaceholder.style.display = 'none';
        } else {
          triggerThumb.hidden = true;
          triggerPlaceholder.style.display = '';
        }
      } else {
        triggerTitle.textContent = this.sinkPlaceholderText;
        triggerPrice.textContent = '';
        triggerPrice.hidden = true;
        triggerThumb.hidden = true;
        triggerThumb.src = '';
        triggerPlaceholder.style.display = '';
      }
    }

    toggleSinkPanel() {
      if (this.sinkPicker.root.hasAttribute('data-open')) this.closeSinkPanel();
      else this.openSinkPanel();
    }

    openSinkPanel() {
      this.sinkPicker.root.setAttribute('data-open', '');
      this.sinkPicker.panel.hidden = false;
      this.sinkPicker.trigger.setAttribute('aria-expanded', 'true');
      // Focus the search box after the layout settles so a screen reader / keyboard
      // user lands directly on the filter input instead of the trigger button.
      setTimeout(() => this.sinkPicker.search?.focus(), 0);
    }

    closeSinkPanel() {
      if (!this.sinkPicker) return;
      this.sinkPicker.root.removeAttribute('data-open');
      this.sinkPicker.panel.hidden = true;
      this.sinkPicker.trigger.setAttribute('aria-expanded', 'false');
      // Reset the search filter so the next open shows the full list
      if (this.sinkPicker.search.value) {
        this.sinkPicker.search.value = '';
        this.renderSinkList('');
      }
    }

    selectSink(handle) {
      const option = this.sinkOptions.find(s => s.handle === handle);
      if (!option) return;
      const sink = this.state.cutouts.sink;
      sink.product_handle = option.handle;
      sink.variant_id = option.variant_id;
      sink.product_title = option.title;
      sink.product_price = option.price;
      this.updateSinkTrigger(option);
      this.applySinkMetafields(option.handle);
      this.updatePrice();
      this.closeSinkPanel();
      // Bring focus back to the trigger so keyboard users continue from a
      // predictable spot after picking.
      this.sinkPicker.trigger.focus();
    }

    escapeAttr(s) {
      return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /** Canonical initial state — also used by reset() */
    getDefaultState() {
      return {
        step: 1,
        formation: 'single_rect',
        main_material: BoardCalculator.DEFAULT_MAIN_MATERIAL,
        material: null,
        unit: 'cm',
        thickness: 27,
        boards: [{ length: '', width: '' }],
        edges: [{ A: 'sharp', B: 'sharp', C: 'sharp', D: 'sharp' }],
        corners: {},
        cutouts: {
          sink: { enabled: false, width: '', depth: '', center_x: '', board_index: 1, product_handle: '', variant_id: null, product_title: '', product_price: 0 },
          hob:  { enabled: false, width: '', depth: '', center_x: '', board_index: 1 }
        },
        surface: 'none',
        // Thomas 25 Aug: the client changed their mind, several packages may now be ticked.
        // Was a single handle; now a list of handles, empty by default.
        service_packages: []
      };
    }

    /** P4: Client feedback — scroll page to calculator top on step change */
    scrollToTop() {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /** P4: Client feedback — "Start forfra" reset opens a styled modal (replaces native window.confirm) */
    bindReset() {
      const btn = this.querySelector('.calculator__nav-btn--reset');
      if (btn) btn.addEventListener('click', () => this.openResetModal());

      const modal = this.querySelector('[data-reset-modal]');
      if (!modal) return;

      modal.querySelectorAll('[data-reset-cancel]').forEach(el => {
        el.addEventListener('click', () => this.closeResetModal());
      });
      modal.querySelector('[data-reset-confirm]')?.addEventListener('click', () => {
        this.closeResetModal();
        this.performReset();
      });
      // Close on Escape
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeResetModal();
      });
    }

    openResetModal() {
      const modal = this.querySelector('[data-reset-modal]');
      if (!modal) return;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Focus the confirm button for keyboard users
      modal.querySelector('[data-reset-confirm]')?.focus();
    }

    closeResetModal() {
      const modal = this.querySelector('[data-reset-modal]');
      if (!modal) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    reset() {
      // Kept as a convenience entry point; the modal-driven flow calls performReset directly.
      this.openResetModal();
    }

    performReset() {
      this.state = this.getDefaultState();
      this.activeCorner = null; // Round 4 (1 May): clear stale highlight ring after reset.

      // Reset formation cards
      this.querySelectorAll('.calculator__formation-card').forEach(c => c.classList.remove('calculator__formation-card--active'));
      const defaultFormation = this.querySelector(`.calculator__formation-card[data-formation="single_rect"]`);
      if (defaultFormation) defaultFormation.classList.add('calculator__formation-card--active');

      // Reset main material back to the default, then re-render the material list for it.
      // Aug 2026: the list is filtered per main material, so it has to be rebuilt rather
      // than just having the active class moved to whatever card happens to be first.
      this.renderMainMaterials();
      this.renderMaterials();
      this.renderPackages();
      const firstMaterial = this.querySelector('.calculator__material-card');
      if (firstMaterial) {
        firstMaterial.classList.add('calculator__material-card--active');
        this.state.material = firstMaterial.dataset.material;
      }

      // Reset unit toggle
      this.querySelectorAll('[data-unit]').forEach(b => b.classList.remove('calculator__toggle-btn--active'));
      this.querySelector('[data-unit="cm"]')?.classList.add('calculator__toggle-btn--active');
      this.querySelectorAll('.calculator__unit-label').forEach(el => el.textContent = 'cm');

      // Reset thickness. Rebuilt from the main material's list rather than reaching for a
      // hardcoded 27mm button, which does not exist when the main material is stone.
      this.renderThicknessOptions();
      this.updateThicknessAvailability();

      // Reset cutout checkboxes + fields
      ['sink', 'hob'].forEach(type => {
        const checkbox = this.querySelector(`[data-cutout="${type}"]`);
        if (checkbox) checkbox.checked = false;
        const fields = this.querySelector(`#cutout-${type}-fields`);
        if (fields) fields.style.display = 'none';
        const err = this.querySelector(`[data-cutout-error="${type}"]`);
        if (err) { err.textContent = ''; err.style.display = 'none'; }
      });

      // Reset surface dropdown
      const surfSel = this.querySelector('#surface-treatment');
      if (surfSel) surfSel.value = 'none';

      // P5 #4 (23 April): reset sink picker. Followup #2 (24 June 2026): the
      // native <select> was replaced with a custom picker; clear the trigger
      // and rerender the option list so the "Vælg en vask…" placeholder shows
      // again when the user lands back on the surface step after a reset.
      this.updateSinkTrigger?.(null);
      if (this.sinkPicker) this.renderSinkList('');

      // Clear preview thumbnail (used for cart image)
      this.previewPng = null;

      // Go back to step 1
      this.goToStep(1);
    }

    /** P3 #23: Debug logger — only outputs when ?debug=calculator is in URL */
    log(action, data) {
      if (new URLSearchParams(window.location.search).has('debug')) {
        console.log(`[BoardCalculator] ${action}`, data || '');
      }
    }

    /** P0 #4/#5: Set default active states for material and thickness on load */
    initDefaultStates() {
      // Aug 2026: thickness buttons are data-driven now, so they have to exist before
      // anything can be highlighted.
      this.renderThicknessOptions();
      const defaultThickness = this.querySelector(`[data-thickness="${this.state.thickness}"]`);
      if (defaultThickness) defaultThickness.classList.add('calculator__thickness-btn--active');

      // Pre-select first material
      const firstMaterial = this.querySelector('.calculator__material-card');
      if (firstMaterial && !this.state.material) {
        firstMaterial.classList.add('calculator__material-card--active');
        this.state.material = firstMaterial.dataset.material;
      }
    }

    /** P1 #6: Bind download PNG button */
    bindDownload() {
      const btn = this.querySelector('.calculator__download-btn');
      if (btn) btn.addEventListener('click', () => this.downloadPNG());
    }

    // ─── Navigation ───────────────────────────────────────────────

    bindNavigation() {
      const prevBtn = this.querySelector('.calculator__nav-btn--prev');
      const nextBtn = this.querySelector('.calculator__nav-btn--next');
      const cartBtn = this.querySelector('.calculator__nav-btn--cart');

      prevBtn?.addEventListener('click', () => this.goToStep(this.state.step - 1));
      nextBtn?.addEventListener('click', () => {
        if (this.validateCurrentStep()) this.goToStep(this.state.step + 1);
      });
      cartBtn?.addEventListener('click', () => this.addToCart());

      // Progress bar step clicks
      this.querySelectorAll('.calculator__progress-step').forEach(btn => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.dataset.step);
          if (step <= this.state.step) this.goToStep(step);
        });
      });
    }

    goToStep(step) {
      if (step < 1 || step > BoardCalculator.TOTAL_STEPS) return;
      this.state.step = step;
      this.log('goToStep', step);

      // Show/hide steps
      this.querySelectorAll('.calculator__step').forEach(el => {
        el.classList.toggle('calculator__step--active', parseInt(el.dataset.calculatorStep) === step);
      });

      // Update progress bar
      this.querySelectorAll('.calculator__progress-step').forEach(btn => {
        const s = parseInt(btn.dataset.step);
        btn.classList.toggle('calculator__progress-step--active', s === step);
        btn.classList.toggle('calculator__progress-step--completed', s < step);
        btn.disabled = s > step;
      });

      // Progress fill
      const fill = this.querySelector('.calculator__progress-fill');
      if (fill) fill.style.width = `${(step / BoardCalculator.TOTAL_STEPS) * 100}%`;

      // Nav buttons
      const prevBtn = this.querySelector('.calculator__nav-btn--prev');
      const nextBtn = this.querySelector('.calculator__nav-btn--next');
      const cartBtn = this.querySelector('.calculator__nav-btn--cart');
      const isLastStep = step === BoardCalculator.TOTAL_STEPS;
      if (prevBtn) prevBtn.disabled = step === 1;
      if (nextBtn) nextBtn.style.display = isLastStep ? 'none' : '';
      if (cartBtn) cartBtn.style.display = isLastStep ? '' : 'none';

      // Render dynamic content for the step. Step numbers shifted +1 from step 3 onward
      // when main material was inserted as step 2.
      if (step === 2) this.renderMainMaterials();
      if (step === 5) {
        this.renderDimensionFields();
        this.updateDimensionHelpers();
      }
      if (step === 6) this.renderEdgeFields();
      if (step === 7) {
        this.renderCutoutBoardSelectors();
        this.renderPackages();
      }

      this.updateSVG();
      this.updatePrice();

      // P4: Client feedback — on step change, scroll to top of calculator
      this.scrollToTop();
    }

    validateCurrentStep() {
      const step = this.state.step;
      if (step === 1 && !this.state.formation) {
        alert('Vælg venligst en formation');
        return false;
      }
      if (step === 2 && !this.state.main_material) {
        alert('Vælg venligst et hovedmateriale');
        return false;
      }
      if (step === 3 && !this.state.material) {
        alert('Vælg venligst et materiale');
        return false;
      }
      if (step === 4 && !this.state.thickness) {
        alert('Vælg venligst en tykkelse');
        return false;
      }
      if (step === 5) {
        return this.validateDimensions();
      }
      if (step === 6) {
        return this.validateCorners();
      }
      if (step === 7) {
        return this.validateCutouts();
      }
      return true;
    }

    // ─── Formation ────────────────────────────────────────────────

    bindFormation() {
      this.querySelectorAll('.calculator__formation-card').forEach(card => {
        card.addEventListener('click', () => {
          this.querySelectorAll('.calculator__formation-card').forEach(c => c.classList.remove('calculator__formation-card--active'));
          card.classList.add('calculator__formation-card--active');
          this.state.formation = card.dataset.formation;
          this.log('formation', this.state.formation);

          // Round 6 audit (5 May 2026): prune state.corners entries that the new
          // formation doesn't expose. Without this, switching e.g. l_top → l_bottom
          // keeps a 1_CD entry alive — renderCorners() hides its dropdown, but
          // calculatePrices() still charges corner_price and addToCart still ships
          // _corner_1_CD_* properties to manufacturing. Same risk on board-count
          // drops (u_sides → l_bottom orphans 3_*).
          const validForFormation = this.formationCorners[this.state.formation] || {};
          Object.keys(this.state.corners).forEach(key => {
            const [bn, ck] = key.split('_');
            const allowed = validForFormation[parseInt(bn, 10)] || [];
            if (!allowed.includes(ck)) delete this.state.corners[key];
          });

          const count = this.formationBoardCounts[this.state.formation];
          this.state.boards = Array.from({ length: count }, (_, i) => this.state.boards[i] || { length: '', width: '' });
          this.state.edges = Array.from({ length: count }, (_, i) => this.state.edges[i] || { A: 'sharp', B: 'sharp', C: 'sharp', D: 'sharp' });

          // Clamp cutout board_index to valid range for the new formation
          ['sink', 'hob'].forEach(type => {
            if (this.state.cutouts[type].board_index > count) {
              this.state.cutouts[type].board_index = count;
            }
          });

          // Disable 18mm for multi-board
          this.updateThicknessAvailability();
          // Aug 2026: stone has no round formation, so switching to or from round changes
          // whether the stone card is selectable in step 2.
          this.renderMainMaterials();
          // Round 4 (1 May audit): always re-render the cutout section. Without this,
          // switching round → non-round while on step 1 leaves the cutouts container
          // hidden when the user later reaches step 6.
          this.renderCutoutBoardSelectors();
          this.updateSVG();
          this.updatePrice();
        });
      });
    }

    // ─── Service packages (phase 8, Aug 2026) ─────────────────────

    /** Packages for a main material. Real Shopify products, so the client controls the
     *  prices and the line-up in admin without touching the calculator. */
    getPackagesFor(handle) {
      const mm = handle || this.state.main_material;
      return (this.config.packages || []).filter(p => (p.main_material || 'wood') === mm);
    }

    /** The chosen packages. Re-resolved from config every time rather than trusting state,
     *  so a package the client removes from the collection cannot linger in a stale
     *  selection and reach the cart at a price that no longer exists.
     *
     *  Thomas 25 Aug asked for NO validation of overlapping combinations: "we do NOT need
     *  some logic to avoid people ordering double measurement ... the guys handling the
     *  order will make sure they do not pay for the extra". So nothing here blocks a
     *  customer ticking both Opmåling and the combination package. */
    getSelectedPackages() {
      const chosen = this.state.service_packages || [];
      return this.getPackagesFor().filter(p => chosen.includes(p.handle));
    }

    renderPackages() {
      const section = this.querySelector('#calculator-packages-section');
      const box = this.querySelector('#calculator-packages');
      if (!section || !box) return;

      const packages = this.getPackagesFor();
      // Nothing configured for this main material: hide the block entirely rather than
      // showing a lone "no package" option, which would only raise questions.
      section.style.display = packages.length ? '' : 'none';
      if (!packages.length) {
        this.state.service_packages = [];
        return;
      }

      // Thomas 25 Aug: checkboxes, and the explicit "Ingen pakke" row is dropped. It only
      // existed to give radio buttons an opt-out; with checkboxes, ticking nothing is the
      // opt-out, so the extra row is just noise.
      const chosen = this.state.service_packages || [];
      box.innerHTML = packages.map(p => {
        const isOn = chosen.includes(p.handle);
        return `
        <label class="calculator__package${isOn ? ' calculator__package--active' : ''}">
          <input type="checkbox" value="${this.escapeAttr(p.handle)}" ${isOn ? 'checked' : ''}>
          <span class="calculator__package-body">
            <span class="calculator__package-title">${p.title}</span>
          </span>
          <span class="calculator__package-price">${this.formatDKK((p.price_cents || 0) / 100)}</span>
        </label>`;
      }).join('');

      box.querySelectorAll('input[type="checkbox"]').forEach(input => {
        input.addEventListener('change', () => {
          const set = new Set(this.state.service_packages || []);
          if (input.checked) set.add(input.value); else set.delete(input.value);
          this.state.service_packages = [...set];
          this.log('service_packages', this.state.service_packages);
          input.closest('.calculator__package')
            ?.classList.toggle('calculator__package--active', input.checked);
          this.updatePrice();
        });
      });
    }

    // ─── Main material (Aug 2026 stone phase) ─────────────────────

    /** The config entry for the currently selected main material.
     *  Falls back to the first entry, then to a wood-shaped object, so a
     *  half-configured metaobject can never leave callers dereferencing null. */
    getMainMaterial() {
      const list = this.config.main_materials || [];
      return list.find(m => m.handle === this.state.main_material)
        || list[0]
        || {
          handle: 'wood', name: 'Træ', thicknesses: '18,27,40', default_thickness: 27,
          allows_round: true, allows_surface_treatment: true,
          corner_price: BoardCalculator.CORNER_PRICE_DEFAULT,
          sink_cutout_price: 1999.20, hob_cutout_price: 639.20,
          assembly_fee: BoardCalculator.ASSEMBLY_FEE_DEFAULT,
          clearance_ac_mm: BoardCalculator.CLEARANCE_AC_MM,
          clearance_bd_mm: BoardCalculator.CLEARANCE_BD_MM,
          sink_collection: 'kokkenvaske', package_collection: ''
        };
    }

    /** Thicknesses available for a main material, as numbers. Stored as the text
     *  "18,27,40" on the metaobject because a comma-separated field is far easier
     *  for the client to edit in admin than a list-of-integers widget. */
    getThicknessesFor(mainMaterial) {
      const raw = (mainMaterial || this.getMainMaterial()).thicknesses || '';
      const parsed = String(raw).split(',').map(t => parseInt(t.trim(), 10)).filter(Boolean);
      return parsed.length ? parsed : [18, 27, 40];
    }

    /** Materials belonging to a main material. An empty main_material on a material
     *  reads as wood, so the 7 pre-existing wood entries need no admin edit. */
    getMaterialsFor(handle) {
      const mm = handle || this.state.main_material;
      return (this.config.materials || []).filter(m => (m.main_material || 'wood') === mm);
    }

    /** Edges belonging to a main material, same empty-reads-as-wood rule. */
    getEdgesFor(handle) {
      const mm = handle || this.state.main_material;
      return (this.config.edges || []).filter(e => (e.main_material || 'wood') === mm);
    }

    /** Default edge handles for a main material.
     *
     *  Sides A, B and D take the first free edge (wood "Skarp kant", stone "1x1mm fas").
     *  Side C prefers a FREE edge_c_only option when one exists: for stone that is
     *  "Ingen", which Thomas confirmed is the default for C. Wood's edge_c_only entries
     *  are the 9½mm and 12mm taps, which cost money, so the rule does not fire there and
     *  wood keeps defaulting to the same free edge on all four sides. Deriving it this way
     *  keeps the rule in the data rather than hardcoding "stone" in JS. */
    getDefaultEdges(handle) {
      const edges = this.getEdgesFor(handle);
      const general = edges.find(e => !e.edge_c_only && !e.price) || edges.find(e => !e.edge_c_only);
      const freeCOnly = edges.find(e => e.edge_c_only && !e.price);
      const base = general?.handle || 'sharp';
      return { A: base, B: base, C: freeCOnly?.handle || base, D: base };
    }

    renderMainMaterials() {
      const grid = this.querySelector('#calculator-main-materials');
      if (!grid) return;
      const list = this.config.main_materials || [];
      if (!list.length) return;

      // Stone has no round formation. Rather than letting the customer choose stone and
      // then bouncing them back to step 1, the card is disabled here with an explanation.
      const isRound = this.state.formation === 'round';
      const note = this.querySelector('#main-material-note');
      let blockedName = '';

      grid.innerHTML = list.map(mm => {
        const blocked = isRound && !mm.allows_round;
        if (blocked && mm.handle === this.state.main_material) blockedName = mm.name;
        return `
        <button type="button"
          class="calculator__main-material-card${mm.handle === this.state.main_material ? ' calculator__main-material-card--active' : ''}"
          data-main-material="${this.escapeAttr(mm.handle)}"
          ${blocked ? 'disabled' : ''}>
          <span class="calculator__main-material-name">${mm.name}</span>
        </button>`;
      }).join('');

      if (note) {
        const show = isRound && list.some(mm => !mm.allows_round);
        note.textContent = show ? (grid.dataset.roundDisabledNote || '') : '';
        note.style.display = show ? '' : 'none';
      }

      // If the formation changed under a main material that no longer allows it, fall
      // back to one that does instead of leaving an impossible combination selected.
      if (blockedName) {
        const fallback = list.find(mm => mm.allows_round);
        if (fallback) this.setMainMaterial(fallback.handle);
        return;
      }

      grid.querySelectorAll('.calculator__main-material-card').forEach(card => {
        card.addEventListener('click', () => {
          if (card.disabled) return;
          this.setMainMaterial(card.dataset.mainMaterial);
        });
      });
    }

    /** Switch main material and reset everything downstream that belonged to the old one.
     *  Skipping any of these resets leaves state referencing handles that no longer exist
     *  in the filtered config, which prices silently as 0 rather than failing loudly. */
    setMainMaterial(handle) {
      if (!handle || handle === this.state.main_material) return;
      this.state.main_material = handle;
      const mm = this.getMainMaterial();
      this.log('main_material', handle);

      // Material: the old handle belongs to the other main material's list.
      const available = this.getMaterialsFor(handle);
      this.state.material = available[0]?.handle || null;

      // Thickness: snap to this main material's default, or its first available.
      const thicknesses = this.getThicknessesFor(mm);
      const preferred = parseInt(mm.default_thickness, 10);
      this.state.thickness = thicknesses.includes(preferred) ? preferred : thicknesses[0];

      // Edges: old handles are not in the new list at all.
      const defaults = this.getDefaultEdges(handle);
      this.state.edges = this.state.edges.map(() => ({ ...defaults }));

      // Surface treatment does not exist for stone.
      if (!mm.allows_surface_treatment) this.state.surface = 'none';

      // The sink collection differs per main material, so a sink chosen under the old
      // one is not orderable under the new one.
      Object.assign(this.state.cutouts.sink, {
        product_handle: '', variant_id: null, product_title: '', product_price: 0
      });

      // Reload the sink list from the new main material's collection. The previously
      // chosen sink was cleared above because it is not orderable under this material.
      if (this.getMainMaterial().sink_collection !== this.loadedSinkCollection) {
        this.updateSinkTrigger?.(null);
        this.loadSinkProducts?.();
      }

      // The other main material's packages are not orderable under this one.
      this.state.service_packages = [];
      this.renderPackages();

      this.renderMainMaterials();
      this.renderMaterials();
      this.renderThicknessOptions();
      this.updateThicknessAvailability();
      this.updateDimensionHelpers();
      this.updateSVG();
      this.updatePrice();
    }

    // ─── Materials ────────────────────────────────────────────────

    renderMaterials() {
      const grid = this.querySelector('#calculator-materials');
      if (!grid || !this.config.materials) return;

      // Aug 2026: only the materials belonging to the chosen main material.
      const materials = this.getMaterialsFor();

      const card = mat => `
        <button type="button" class="calculator__material-card${mat.handle === this.state.material ? ' calculator__material-card--active' : ''}" data-material="${this.escapeAttr(mat.handle)}">
          ${mat.image
            ? `<img class="calculator__material-image" src="${mat.image}" alt="${mat.name}" loading="lazy">`
            : `<div class="calculator__material-image" style="background: ${this.getMaterialColor(mat.handle)};"></div>`
          }
          <span class="calculator__material-name">${mat.name}</span>
        </button>`;

      // Stone arrives in two groups (Keramik, Komposit) and Thomas asked for them to be
      // shown grouped rather than as one list of 21. Wood has no group value and renders
      // flat, exactly as before. Group order follows first appearance in the config.
      const groups = [];
      materials.forEach(mat => {
        const key = mat.group || '';
        let g = groups.find(x => x.key === key);
        if (!g) groups.push(g = { key, items: [] });
        g.items.push(mat);
      });

      grid.innerHTML = (groups.length === 1 && !groups[0].key)
        ? groups[0].items.map(card).join('')
        : groups.map(g => `
            ${g.key ? `<h3 class="calculator__material-group-title">${g.key}</h3>` : ''}
            ${g.items.map(card).join('')}
          `).join('');

      grid.querySelectorAll('.calculator__material-card').forEach(card => {
        card.addEventListener('click', () => {
          grid.querySelectorAll('.calculator__material-card').forEach(c => c.classList.remove('calculator__material-card--active'));
          card.classList.add('calculator__material-card--active');
          this.state.material = card.dataset.material;
          this.log('material', this.state.material);
          this.updateDimensionHelpers();
          // Round 4 (1 May audit): re-evaluate thickness availability — if the new
          // material is missing a price for the currently-selected thickness, snap
          // the user to a thickness that does have a price.
          this.updateThicknessAvailability();
          this.updatePrice();
        });
      });
    }

    getMaterialColor(handle) {
      const colors = {
        oak: '#c8a96e', ash: '#d4c5a0', beech: '#deb887',
        walnut: '#5c4033', cherry: '#8b4513', mahogany: '#6b3a2a',
        pine: '#e8d5a3'
      };
      return colors[handle] || '#c8a96e';
    }

    // ─── Units & Thickness ────────────────────────────────────────

    bindUnits() {
      this.querySelectorAll('[data-unit]').forEach(btn => {
        btn.addEventListener('click', () => {
          const oldUnit = this.state.unit;
          const newUnit = btn.dataset.unit;
          if (oldUnit === newUnit) return;

          this.querySelectorAll('[data-unit]').forEach(b => b.classList.remove('calculator__toggle-btn--active'));
          btn.classList.add('calculator__toggle-btn--active');
          this.state.unit = newUnit;
          this.querySelectorAll('.calculator__unit-label').forEach(el => el.textContent = newUnit);

          // P0 #3: Convert existing dimension values
          this.convertDimensionValues(oldUnit, newUnit);
        });
      });
    }

    /** Convert all entered dimension/cutout/corner values between cm and mm */
    convertDimensionValues(fromUnit, toUnit) {
      const convert = (val) => {
        if (!val) return '';
        const num = parseFloat(val.replace(',', '.'));
        if (isNaN(num) || num === 0) return '';
        if (fromUnit === 'cm' && toUnit === 'mm') {
          return String(Math.round(num * 10));
        } else {
          // mm → cm
          const result = num / 10;
          return String(result).replace('.', ',');
        }
      };

      // Convert board dimensions
      this.state.boards.forEach(board => {
        board.length = convert(board.length);
        board.width = convert(board.width);
      });

      // Convert cutout values
      ['sink', 'hob'].forEach(type => {
        const c = this.state.cutouts[type];
        c.width = convert(c.width);
        c.depth = convert(c.depth);
        c.center_x = convert(c.center_x);
      });

      // Convert corner values
      Object.values(this.state.corners).forEach(corner => {
        corner.length = convert(corner.length);
        corner.depth = convert(corner.depth);
      });

      // Re-render dimension fields if currently on step 4
      if (this.state.step === 4) this.renderDimensionFields();
      // Re-render edge/corner fields if currently on step 5
      if (this.state.step === 5) this.renderEdgeFields();

      this.updateSVG();
      this.updatePrice();
    }

    bindThickness() {
      this.querySelectorAll('[data-thickness]').forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          this.querySelectorAll('[data-thickness]').forEach(b => b.classList.remove('calculator__thickness-btn--active'));
          btn.classList.add('calculator__thickness-btn--active');
          this.state.thickness = parseInt(btn.dataset.thickness);
          this.updateDimensionHelpers();
          this.updatePrice();
        });
      });
    }

    /** Aug 2026: thickness buttons come from the main material's `thicknesses` field
     *  (wood 18/27/40, stone 12/20) instead of the three hardcoded buttons that used to
     *  live in calculator-step-units.liquid. Re-rendered whenever the main material
     *  changes; bindThickness() is re-run because the old nodes are gone. */
    renderThicknessOptions() {
      const group = this.querySelector('#calculator-thickness');
      if (!group) return;
      const thicknesses = this.getThicknessesFor();
      if (!thicknesses.includes(this.state.thickness)) this.state.thickness = thicknesses[0];

      group.innerHTML = thicknesses.map(t => `
        <button type="button"
          class="calculator__thickness-btn${t === this.state.thickness ? ' calculator__thickness-btn--active' : ''}"
          data-thickness="${t}">${t}mm</button>`).join('');

      this.bindThickness();
    }

    updateThicknessAvailability() {
      const isMulti = this.formationBoardCounts[this.state.formation] > 1;
      const thicknesses = this.getThicknessesFor();
      const btn18 = this.querySelector('[data-thickness="18"]');
      const note = this.querySelector('#thickness-note');

      if (btn18) {
        btn18.disabled = isMulti;
        if (isMulti && this.state.thickness === 18) {
          btn18.classList.remove('calculator__thickness-btn--active');
          this.state.thickness = 27;
          this.querySelector('[data-thickness="27"]')?.classList.add('calculator__thickness-btn--active');
        }
      }
      // The "no 18mm on multi-board" rule is a wood joinery constraint. It stays keyed to
      // 18mm and simply never fires for stone, whose thicknesses are 12 and 20; Thomas
      // confirmed both stone thicknesses work in every formation except round.
      if (note) note.style.display = isMulti && thicknesses.includes(18) ? '' : 'none';

      // Round 4 (1 May audit): also disable thickness buttons that have no price
      // for the currently-selected material (defends against a misconfigured
      // metaobject silently returning 0 DKK to the customer). If the customer
      // is sitting on a now-unavailable thickness, snap them to the next available.
      const mat = this.config.materials?.find(m => m.handle === this.state.material);
      thicknesses.forEach(t => {
        const btn = this.querySelector(`[data-thickness="${t}"]`);
        if (!btn || (t === 18 && isMulti)) return; // multi-board rule already handled above
        const priceKey = `price_${t}mm`;
        const hasPrice = mat && typeof mat[priceKey] === 'number' && mat[priceKey] > 0;
        btn.disabled = !hasPrice;
        btn.title = hasPrice ? '' : `Ingen pris konfigureret for ${t}mm i ${mat?.name || 'dette materiale'}`;
      });
      // If current thickness is now disabled, snap to first available.
      const currentBtn = this.querySelector(`[data-thickness="${this.state.thickness}"]`);
      if (currentBtn && currentBtn.disabled) {
        const firstAvail = this.querySelector('[data-thickness]:not([disabled])');
        if (firstAvail) {
          this.querySelectorAll('[data-thickness]').forEach(b => b.classList.remove('calculator__thickness-btn--active'));
          firstAvail.classList.add('calculator__thickness-btn--active');
          this.state.thickness = parseInt(firstAvail.dataset.thickness, 10);
        }
      }
    }

    // ─── Dimensions ───────────────────────────────────────────────

    bindDimensions() {
      this.querySelector('#calculator-boards')?.addEventListener('input', (e) => {
        if (!e.target.classList.contains('calculator__dim-input')) return;
        e.target.value = this.sanitizeDimensionInput(e.target.value);
        const board = parseInt(e.target.dataset.board) - 1;
        const dim = e.target.dataset.dim;
        if (this.state.boards[board]) {
          this.state.boards[board][dim] = e.target.value;
        }
        this.updateSVG();
        this.updatePrice();
      });
    }

    sanitizeDimensionInput(value) {
      // Allow only digits, comma, and period
      let v = value.replace(/[^0-9.,]/g, '');
      // Normalize: replace period with comma for display consistency
      const sep = v.includes(',') ? ',' : (v.includes('.') ? '.' : '');
      if (!sep) return v;

      const parts = v.split(sep);
      if (this.state.unit === 'mm') {
        // mm: integers only — strip everything after separator
        return parts[0];
      }
      // cm: max 1 decimal
      return parts[0] + sep + (parts[1] || '').slice(0, 1);
    }

    renderDimensionFields() {
      const container = this.querySelector('#calculator-boards');
      if (!container) return;

      const count = this.formationBoardCounts[this.state.formation];
      const isRound = this.state.formation === 'round';
      const unit = this.state.unit;

      container.innerHTML = Array.from({ length: count }, (_, i) => {
        const board = this.state.boards[i] || { length: '', width: '' };
        const boardNum = i + 1;

        if (isRound) {
          return `
            <div class="calculator__board-fields" data-board="${boardNum}">
              <div class="calculator__dimension-row">
                <div class="calculator__field-col">
                  <label class="calculator__field form__field">
                    <input type="text" class="form__field__input calculator__dim-input" data-board="${boardNum}" data-dim="length" placeholder=" " inputmode="decimal" value="${board.length}">
                    <span class="form__field__label">Diameter (${unit})</span>
                  </label>
                  <p class="calculator__field-help" data-board-help="${boardNum}" data-dim="diameter"></p>
                </div>
              </div>
              <p class="calculator__dim-error" data-board-error="${boardNum}" style="display:none;"></p>
            </div>
          `;
        }

        return `
          <div class="calculator__board-fields" data-board="${boardNum}">
            ${count > 1 ? `<h3 class="calculator__board-heading">Plade ${boardNum}</h3>` : ''}
            <div class="calculator__dimension-row">
              <div class="calculator__field-col">
                <label class="calculator__field form__field">
                  <input type="text" class="form__field__input calculator__dim-input" data-board="${boardNum}" data-dim="length" placeholder=" " inputmode="decimal" value="${board.length}">
                  <span class="form__field__label">Længde (${unit})</span>
                </label>
                <p class="calculator__field-help" data-board-help="${boardNum}" data-dim="length"></p>
              </div>
              <div class="calculator__field-col">
                <label class="calculator__field form__field">
                  <input type="text" class="form__field__input calculator__dim-input" data-board="${boardNum}" data-dim="width" placeholder=" " inputmode="decimal" value="${board.width}">
                  <span class="form__field__label">Bredde (${unit})</span>
                </label>
                <p class="calculator__field-help" data-board-help="${boardNum}" data-dim="width"></p>
              </div>
            </div>
            <p class="calculator__dim-error" data-board-error="${boardNum}" style="display:none;"></p>
          </div>
        `;
      }).join('');

      this.updateDimensionHelpers();
    }

    /** P4: Client feedback — live min/max helper text below each dimension input, in the user's selected unit */
    updateDimensionHelpers() {
      const lim = this.getMaterialLimits();
      const isRound = this.state.formation === 'round';
      const globalMinLong = this.config.min_board_length_mm || BoardCalculator.MIN_LENGTH_DEFAULT;
      const globalMinShort = this.config.min_board_width_mm || BoardCalculator.MIN_WIDTH_DEFAULT;

      this.querySelectorAll('.calculator__field-help[data-board-help]').forEach(el => {
        const dim = el.dataset.dim;
        let text = '';
        if (dim === 'diameter') {
          text = `Min: ${this.formatLimit(lim.minD)}, Max: ${this.formatLimit(lim.maxD)}`;
        } else if (isRound) {
          // rectangle helpers hidden when formation is round
          text = '';
        } else if (dim === 'length') {
          text = `Min: ${this.formatLimit(globalMinLong)}, Max: ${this.formatLimit(lim.maxL)}`;
        } else if (dim === 'width') {
          text = `Min: ${this.formatLimit(globalMinShort)}, Max: ${this.formatLimit(lim.maxW)}`;
        }
        el.textContent = text;
        el.style.display = text ? '' : 'none';
      });
    }

    parseDimension(value) {
      if (!value) return 0;
      return parseFloat(value.replace(',', '.')) || 0;
    }

    /** Format a value stored in mm into the user's currently-selected display unit. */
    formatLimit(mm) {
      if (this.state.unit === 'cm') {
        const cm = mm / 10;
        const txt = Number.isInteger(cm) ? String(cm) : cm.toFixed(1).replace('.', ',');
        return `${txt} cm`;
      }
      return `${mm} mm`;
    }

    getDimensionMM(board) {
      const l = this.parseDimension(board.length);
      const w = this.parseDimension(board.width);
      const factor = this.state.unit === 'cm' ? 10 : 1;
      return { length: l * factor, width: w * factor };
    }

    /** Get material-specific dimension limits for current material + thickness */
    getMaterialLimits() {
      const mat = this.config.materials?.find(m => m.handle === this.state.material);
      const thickness = String(this.state.thickness);
      const limits = mat?.limits?.[thickness];
      if (limits) return limits;
      // Fallback to global defaults
      return {
        minL: this.config.min_board_width_mm || BoardCalculator.MIN_WIDTH_DEFAULT,
        maxL: 4530,
        minW: this.config.min_board_width_mm || BoardCalculator.MIN_WIDTH_DEFAULT,
        maxW: 1010,
        minD: BoardCalculator.MIN_LENGTH_DEFAULT,
        maxD: 1200
      };
    }

    validateDimensions() {
      let valid = true;
      const globalMinLong = this.config.min_board_length_mm || BoardCalculator.MIN_LENGTH_DEFAULT;
      const globalMinShort = this.config.min_board_width_mm || BoardCalculator.MIN_WIDTH_DEFAULT;
      const lim = this.getMaterialLimits();

      this.state.boards.forEach((board, i) => {
        const { length, width } = this.getDimensionMM(board);
        const errorEl = this.querySelector(`[data-board-error="${i + 1}"]`);
        let error = '';

        if (this.state.formation === 'round') {
          if (!length) {
            error = 'Indtast venligst diameter';
          } else if (length < lim.minD) {
            error = `Minimum diameter: ${this.formatLimit(lim.minD)}`;
          } else if (length > lim.maxD) {
            error = `Maksimum diameter: ${this.formatLimit(lim.maxD)}`;
          }
        } else {
          if (!length || !width) {
            error = 'Indtast venligst begge mål';
          } else {
            // Global rule: at least one dimension >= 300mm, other >= 100mm
            const longerSide = Math.max(length, width);
            const shorterSide = Math.min(length, width);
            if (longerSide < globalMinLong) {
              error = `Mindst ét mål skal være min. ${this.formatLimit(globalMinLong)}`;
            } else if (shorterSide < globalMinShort) {
              error = `Minimum mål: ${this.formatLimit(globalMinShort)}`;
            }
            // Material-specific max checks. Hint at the two real escape hatches:
            // pick a wider material, or split into a multi-board formation.
            else if (length > lim.maxL) {
              // Thomas 25 Aug: point the customer at a conversation about length joining
              // instead of telling them to pick a different material. Applies to wood and
              // stone alike; the width message below is deliberately left as it was.
              error = `Maks. længden er ${this.formatLimit(lim.maxL)} – Kontakt os på mail / tlf., så rådgiver vi om længdesamling.`;
            } else if (width > lim.maxW) {
              error = `Maks. bredde for dette materiale: ${this.formatLimit(lim.maxW)}. Vælg et andet materiale eller en flerplade-formation.`;
            }
          }
        }

        if (errorEl) {
          errorEl.textContent = error;
          errorEl.style.display = error ? '' : 'none';
        }
        if (error) valid = false;
      });

      return valid;
    }

    // ─── Edges ────────────────────────────────────────────────────

    renderEdgeFields() {
      const container = this.querySelector('#calculator-edges');
      if (!container) return;

      const count = this.formationBoardCounts[this.state.formation];
      // Aug 2026: only this main material's edges. Stone offers "1x1mm fas" on A, B and D
      // and additionally "Ingen" on C; wood keeps its full list. Thomas asked for the
      // single-option sides to still show a dropdown, chosen and not changeable, so any
      // side that resolves to one option renders disabled rather than as plain text.
      const edges = this.getEdgesFor();
      const sides = ['A', 'B', 'C', 'D'];
      // Thomas 25 Aug: stone must offer "Ingen" on A, B and D too, not just C, while the
      // defaults stay as they are (Synlig kant on A/B/D, Ingen on C).
      //
      // `edge_c_only` means "a C-only EXTRA". Wood's two taps are exactly that and cost
      // money. Stone's "Ingen" carries the same flag but is free, and a free alternative is
      // never an extra, so it belongs on every side. Expressing it as "priced C-only options
      // stay on C, free ones appear everywhere" keeps the rule in the data and leaves wood
      // untouched, rather than needing a new admin field or a hardcoded check for stone.
      const optionsFor = side => edges.filter(e => !e.edge_c_only || side === 'C' || !e.price);
      const renderSelect = (side, boardNum, selected) => {
        const opts = optionsFor(side);
        const locked = opts.length <= 1;
        return `<select class="calculator__edge-select" data-board="${boardNum}" data-side="${side}"${locked ? ' disabled' : ''}>
          ${opts.map(e => `<option value="${e.handle}" ${selected === e.handle ? 'selected' : ''}>${e.name}${e.price > 0 ? ` (${this.formatDKK(this.inclVat(e.price))})` : ''}</option>`).join('')}
        </select>`;
      };
      // Hotfix (28 April): per-board dynamic side labels — A is always "front", B/C/D
      // get the SVG position (top/right/bund/venstre) read from formationLabels for that board.
      // This stays correct for every formation (horizontal vs vertical boards) instead of
      // hardcoding "B=højre, D=venstre" which only matched single_rect under the old scheme.
      const positionDanish = { top: 'top', right: 'højre', bottom: 'bund', left: 'venstre' };

      // Defensive: if state still carries handles from the other main material (possible when
      // stepping backwards), snap them to this one's defaults so the selects are never blank
      // and pricing never silently drops an edge it cannot find.
      const fallbackEdges = this.getDefaultEdges();
      const validHandles = new Set(edges.map(e => e.handle));
      this.state.edges = this.state.edges.map(be => {
        if (be && sides.every(sd => validHandles.has(be[sd]))) return be;
        return { ...fallbackEdges };
      });

      container.innerHTML = Array.from({ length: count }, (_, i) => {
        const boardEdges = this.state.edges[i] || { ...fallbackEdges };
        const boardNum = i + 1;
        const boardFormationLabels = this.formationLabels[this.state.formation]?.[boardNum] || { bottom: 'A', left: 'B', top: 'C', right: 'D' };
        const sideToPosition = {};
        for (const [pos, letter] of Object.entries(boardFormationLabels)) sideToPosition[letter] = pos;
        const sideLabels = {
          A: `Side A (forkant)`,
          B: `Side B${sideToPosition.B ? ` (${positionDanish[sideToPosition.B]})` : ''}`,
          C: `Side C${sideToPosition.C ? ` (bagkant)` : ''}`,
          D: `Side D${sideToPosition.D ? ` (${positionDanish[sideToPosition.D]})` : ''}`
        };

        if (this.state.formation === 'round') {
          return `
            <div class="calculator__edge-board">
              <div class="calculator__edge-row">
                <span class="calculator__edge-label">Kant</span>
                ${renderSelect('A', boardNum, boardEdges.A)}
              </div>
            </div>
          `;
        }

        return `
          <div class="calculator__edge-board">
            ${count > 1 ? `<div class="calculator__edge-board-title">Plade ${boardNum}</div>` : ''}
            ${sides.map(side => `
              <div class="calculator__edge-row">
                <span class="calculator__edge-label">${sideLabels[side]}</span>
                ${renderSelect(side, boardNum, boardEdges[side])}
              </div>
            `).join('')}
          </div>
        `;
      }).join('');

      // Bind edge selects
      container.querySelectorAll('.calculator__edge-select').forEach(select => {
        select.addEventListener('change', () => {
          const board = parseInt(select.dataset.board) - 1;
          const side = select.dataset.side;
          if (this.state.edges[board]) {
            this.state.edges[board][side] = select.value;
          }
          this.updatePrice();
        });
      });

      // Render corners
      this.renderCorners();
    }

    renderCorners() {
      const container = this.querySelector('#calculator-corners');
      // Thomas 25 Aug: this label showed the wood price on stone boards. The charge was
      // always correct (calculatePrices uses the main material), only the label was wrong.
      // Same class of bug as the cutout price labels.
      const cornerPrice = this.getMainMaterial().corner_price
        ?? this.config.corner_price ?? BoardCalculator.CORNER_PRICE_DEFAULT;
      if (!container) return;

      if (this.state.formation === 'round') {
        container.innerHTML = '<p style="opacity:0.6;font-size:0.875rem;">Ikke tilgængelig for runde plader</p>';
        return;
      }

      const count = this.formationBoardCounts[this.state.formation];
      const cornerNames = { AB: 'Hjørne A-B', BC: 'Hjørne B-C', CD: 'Hjørne C-D', DA: 'Hjørne D-A' };
      const unit = this.state.unit;
      const validCorners = this.formationCorners[this.state.formation] || {};

      container.innerHTML = Array.from({ length: count }, (_, i) => {
        const boardNum = i + 1;
        // P1 #12: Only show corners that exist for this formation/board
        const boardCorners = validCorners[boardNum] || [];
        if (boardCorners.length === 0) return '';
        return `
          ${count > 1 ? `<h4 style="font-size:0.9375rem;font-weight:600;margin:1rem 0 0.5rem;">Plade ${boardNum}</h4>` : ''}
          ${boardCorners.map(corner => {
            const key = `${boardNum}_${corner}`;
            const data = this.state.corners[key] || { type: 'none', length: '', depth: '' };
            return `
              <div class="calculator__corner-item">
                <div class="calculator__corner-header">
                  <span class="calculator__corner-label">${cornerNames[corner]}</span>
                  <select class="calculator__corner-select" data-corner-key="${key}">
                    <option value="none" ${data.type === 'none' ? 'selected' : ''}>Ingen udskæring</option>
                    <option value="square" ${data.type === 'square' ? 'selected' : ''}>Firkantet indhak (${this.formatDKK(this.inclVat(cornerPrice))})</option>
                  </select>
                </div>
                <div class="calculator__corner-fields" data-corner-fields="${key}" style="${data.type === 'square' ? '' : 'display:none;'}">
                  <label class="calculator__field form__field">
                    <input type="text" class="form__field__input calculator__corner-input" data-corner-key="${key}" data-dim="length" placeholder=" " inputmode="decimal" value="${data.length}">
                    <span class="form__field__label">Længde (${unit})</span>
                  </label>
                  <label class="calculator__field form__field">
                    <input type="text" class="form__field__input calculator__corner-input" data-corner-key="${key}" data-dim="depth" placeholder=" " inputmode="decimal" value="${data.depth}">
                    <span class="form__field__label">Dybde (${unit})</span>
                  </label>
                </div>
              </div>
            `;
          }).join('')}
        `;
      }).join('');

      // Bind corner selects
      container.querySelectorAll('.calculator__corner-select').forEach(select => {
        select.addEventListener('change', () => {
          const key = select.dataset.cornerKey;
          if (!this.state.corners[key]) this.state.corners[key] = { type: 'none', length: '', depth: '' };
          this.state.corners[key].type = select.value;
          const fields = this.querySelector(`[data-corner-fields="${key}"]`);
          if (fields) fields.style.display = select.value === 'square' ? '' : 'none';
          this.updateSVG();
          this.updatePrice();
        });
        // P5 #2 (23 April): focus on the select activates the corner highlight on the SVG
        select.addEventListener('focus', () => { this.activeCorner = select.dataset.cornerKey; this.updateSVG(); });
      });

      // Bind corner inputs — P1 #7: sanitize like dimension inputs
      container.querySelectorAll('.calculator__corner-input').forEach(input => {
        input.addEventListener('input', () => {
          input.value = this.sanitizeDimensionInput(input.value);
          const key = input.dataset.cornerKey;
          if (!this.state.corners[key]) this.state.corners[key] = { type: 'square', length: '', depth: '' };
          this.state.corners[key][input.dataset.dim] = input.value;
          this.updateSVG(); // P5 #2 redraw to update the corner notch in real time
          this.updatePrice();
        });
        // P5 #2 (23 April): focus tracks active corner for the SVG highlight
        input.addEventListener('focus', () => { this.activeCorner = input.dataset.cornerKey; this.updateSVG(); });
      });

      // Clear active highlight when focus leaves the corner section entirely
      container.addEventListener('focusout', (e) => {
        if (!container.contains(e.relatedTarget)) {
          this.activeCorner = null;
          this.updateSVG();
        }
      });
    }

    /** P1 #8: Validate corner dimensions — must be > 0 if corner type is 'square'.
     *  Round 4 (1 May audit): also reject corner cuts that exceed the host board's
     *  length × width (otherwise the SVG renders a notch wider than the whole board)
     *  AND reject pairs of corners on the same edge whose lengths sum past the edge.
     */
    validateCorners() {
      let valid = true;
      const factor = this.state.unit === 'cm' ? 10 : 1;

      // Group corners by board for the per-edge sum check.
      const cornersByBoard = {};
      Object.entries(this.state.corners).forEach(([key, c]) => {
        if (c.type !== 'square') return;
        const [bn] = key.split('_');
        cornersByBoard[bn] = cornersByBoard[bn] || [];
        cornersByBoard[bn].push({ key, corner: c });
      });

      // Per-corner: dimensions must be > 0 and not exceed the host board.
      Object.entries(this.state.corners).forEach(([key, corner]) => {
        if (corner.type !== 'square') return;
        const l_mm = this.parseDimension(corner.length) * factor;
        const d_mm = this.parseDimension(corner.depth)  * factor;
        const [bnStr, cornerKey] = key.split('_');
        const bn = parseInt(bnStr, 10);
        const board = this.getDimensionMM(this.state.boards[bn - 1] || { length: '', width: '' });
        const cornerLabel = `Hjørne ${cornerKey[0]}-${cornerKey[1]} (plade ${bn})`;

        if (l_mm <= 0 || d_mm <= 0) {
          valid = false;
          alert(`Indtast venligst mål for ${cornerLabel}`);
          return;
        }
        if (l_mm > board.length || d_mm > board.width) {
          valid = false;
          alert(`${cornerLabel}: målene må ikke overskride pladen (max. længde ${this.formatLimit(board.length)}, max. dybde ${this.formatLimit(board.width)})`);
        }
      });

      // Per-board: pairs of corners on the same edge must not overlap.
      // Adjacent corners share an edge if their corner-keys share a side letter.
      // E.g. 'AB' and 'DA' both touch side A → their lengths along side A must
      // sum to <= length of side A. Same for 'AB' and 'BC' touching side B, etc.
      Object.entries(cornersByBoard).forEach(([bnStr, list]) => {
        if (list.length < 2) return;
        const bn = parseInt(bnStr, 10);
        const board = this.getDimensionMM(this.state.boards[bn - 1] || { length: '', width: '' });
        // For each pair, check if they share a side letter.
        for (let i = 0; i < list.length; i++) {
          for (let j = i + 1; j < list.length; j++) {
            const k1 = list[i].key.split('_')[1]; // e.g. 'AB'
            const k2 = list[j].key.split('_')[1];
            const shared = [...k1].find(letter => k2.includes(letter));
            if (!shared) continue;
            // Sides A and C run along the LENGTH axis; B and D along the WIDTH axis.
            const edgeMm = (shared === 'A' || shared === 'C') ? board.length : board.width;
            // The "length" of each corner cut along the shared side: by convention,
            // for a corner XY, the cut extends `length` along side X and `depth` along side Y.
            // Since the user enters length/depth without spatial context, the SAFE check is
            // length+length, and length+depth and depth+depth — accept the worst case.
            const c1 = list[i].corner, c2 = list[j].corner;
            const l1 = this.parseDimension(c1.length) * factor;
            const d1 = this.parseDimension(c1.depth)  * factor;
            const l2 = this.parseDimension(c2.length) * factor;
            const d2 = this.parseDimension(c2.depth)  * factor;
            // Whichever the user means, taking max(l, d) of each gives the safe upper bound.
            const span1 = Math.max(l1, d1);
            const span2 = Math.max(l2, d2);
            if (span1 + span2 > edgeMm) {
              valid = false;
              alert(`Hjørne ${k1} og ${k2} på plade ${bn} overlapper: deres mål på siden ${shared} (${this.formatLimit(span1)} + ${this.formatLimit(span2)}) overstiger sidens længde (${this.formatLimit(edgeMm)}).`);
              return;
            }
          }
        }
      });

      return valid;
    }

    // ─── Cutouts ──────────────────────────────────────────────────

    bindCutouts() {
      ['sink', 'hob'].forEach(type => {
        const checkbox = this.querySelector(`[data-cutout="${type}"]`);
        const fields = this.querySelector(`#cutout-${type}-fields`);
        if (!checkbox || !fields) return;

        checkbox.addEventListener('change', () => {
          this.state.cutouts[type].enabled = checkbox.checked;
          fields.style.display = checkbox.checked ? '' : 'none';
          if (checkbox.checked) {
            // Default to highest-numbered board for multi-board formations
            const count = this.formationBoardCounts[this.state.formation];
            this.state.cutouts[type].board_index = count;
            this.renderCutoutBoardSelectors();
          }
          this.updatePrice();
          this.updateSVG();
        });
      });

      this.querySelectorAll('.calculator__cutout-input').forEach(input => {
        input.addEventListener('input', () => {
          input.value = this.sanitizeDimensionInput(input.value);
          const type = input.dataset.cutout;
          const dim = input.dataset.dim;
          this.state.cutouts[type][dim] = input.value;
          this.updateSVG();
          this.updatePrice();
        });
      });

      // Board-index dropdowns — event delegation so re-renders keep working
      this.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-cutout-board]');
        if (!sel) return;
        const type = sel.dataset.cutoutBoard;
        const idx = parseInt(sel.value, 10);
        if (type && idx > 0) {
          this.state.cutouts[type].board_index = idx;
          this.updateSVG();
          this.updatePrice();
        }
      });
    }

    /** P4: Client feedback — show "På plade" dropdown per cutout when formation has >1 board */
    renderCutoutBoardSelectors() {
      const count = this.formationBoardCounts[this.state.formation];
      const isRound = this.state.formation === 'round';

      // Round 4 (1 May audit): cutouts (sink + hob) don't make sense on a round board —
      // the rectangular clearance check fires nonsensically and the SVG can't render
      // a notch on a circle. Hide the whole "Udskæringer" section, force-disable both,
      // and clear any error messages so the customer doesn't see stale state.
      const cutoutsContainer = this.querySelector('.calculator__cutouts-container');
      if (cutoutsContainer) cutoutsContainer.style.display = isRound ? 'none' : '';
      if (isRound) {
        ['sink', 'hob'].forEach(type => {
          this.state.cutouts[type].enabled = false;
          const checkbox = this.querySelector(`[data-cutout="${type}"]`);
          if (checkbox) checkbox.checked = false;
          const fields = this.querySelector(`#cutout-${type}-fields`);
          if (fields) fields.style.display = 'none';
          const err = this.querySelector(`[data-cutout-error="${type}"]`);
          if (err) { err.textContent = ''; err.style.display = 'none'; }
        });
      }

      ['sink', 'hob'].forEach(type => {
        const wrapper = this.querySelector(`[data-cutout-board-wrapper="${type}"]`);
        const select = this.querySelector(`[data-cutout-board="${type}"]`);
        if (!wrapper || !select) return;

        if (count <= 1) {
          wrapper.style.display = 'none';
          this.state.cutouts[type].board_index = 1;
          return;
        }

        wrapper.style.display = '';
        const current = this.state.cutouts[type].board_index || count;
        select.innerHTML = Array.from({ length: count }, (_, i) => {
          const n = i + 1;
          return `<option value="${n}" ${n === current ? 'selected' : ''}>Plade ${n}</option>`;
        }).join('');
      });
    }

    validateCutouts() {
      let valid = true;

      ['sink', 'hob'].forEach(type => {
        if (!this.state.cutouts[type].enabled) return;

        const cutout = this.state.cutouts[type];
        const w = this.parseDimension(cutout.width);
        const d = this.parseDimension(cutout.depth);
        const cx = this.parseDimension(cutout.center_x);
        const factor = this.state.unit === 'cm' ? 10 : 1;

        const cutW_mm = w * factor;
        const cutD_mm = d * factor;
        const cx_mm = cx * factor;

        // Use the user-selected target board's dimensions for clearance check
        const targetIdx = Math.max(0, (cutout.board_index || 1) - 1);
        const board = this.getDimensionMM(this.state.boards[targetIdx] || { length: '', width: '' });
        const boardL = board.length;
        const boardW = board.width;

        const errorEl = this.querySelector(`[data-cutout-error="${type}"]`);
        let error = '';

        // P5 #4 (23 April) — sink also needs a chosen sink product. Check first so
        // it isn't clobbered by the dimension-OK path below.
        if (type === 'sink' && !cutout.variant_id) {
          error = 'Vælg venligst en vask, eller fjern vaskudskæringen';
        } else if (!w || !d || !cx) {
          error = 'Indtast venligst alle mål for udskæringen';
        } else {
          // Cutout placed at fixed distance from front edge A
          // Clearance requirements per spec (minimum_space_draft.pdf)
          // Aug 2026: clearances read from the main material. Wood and stone are identical
          // today by Thomas's choice, but they are wired per material so changing stone
          // later is an admin edit rather than a code change.
          const mmc = this.getMainMaterial();
          const cutoutY = BoardCalculator.CUTOUT_Y_MM;
          const minAC = mmc.clearance_ac_mm ?? BoardCalculator.CLEARANCE_AC_MM;
          const minBD = mmc.clearance_bd_mm ?? BoardCalculator.CLEARANCE_BD_MM;

          const backClearance = boardW - (cutoutY + cutD_mm); // distance to edge opposite A
          const leftClearance = cx_mm - cutW_mm / 2;          // distance from cutout to start of A
          const rightClearance = boardL - (cx_mm + cutW_mm / 2); // distance from cutout to end of A

          // Round 3 (1 May): cx_mm is uniformly "distance from AB-corner along A clockwise",
          // so the clearance-to-side mapping is universal: leftClearance → side B, rightClearance
          // → side D, backClearance → opposite-of-A → side C (always, since A's opposite letter
          // in a clockwise A→B→C→D order is C).
          if (backClearance < minAC) error = `Udskæringen er for tæt på side C (min. ${this.formatLimit(minAC)})`;
          else if (leftClearance < minBD) error = `Udskæringen er for tæt på side B (min. ${this.formatLimit(minBD)})`;
          else if (rightClearance < minBD) error = `Udskæringen er for tæt på side D (min. ${this.formatLimit(minBD)})`;
        }

        if (errorEl) {
          errorEl.textContent = error;
          errorEl.style.display = error ? '' : 'none';
        }
        if (error) valid = false;
      });

      // Round 4 (1 May audit): if sink AND hob are both enabled on the same board,
      // their cx ranges along side A must not overlap (otherwise we can't actually
      // mill both — they'd cut into each other). Tolerance: require >= 50mm gap.
      const sink = this.state.cutouts.sink;
      const hob  = this.state.cutouts.hob;
      const MIN_GAP_MM = 50;
      if (sink.enabled && hob.enabled && (sink.board_index || 1) === (hob.board_index || 1)) {
        const factor = this.state.unit === 'cm' ? 10 : 1;
        const sinkCx = this.parseDimension(sink.center_x) * factor;
        const sinkW  = this.parseDimension(sink.width)    * factor;
        const hobCx  = this.parseDimension(hob.center_x)  * factor;
        const hobW   = this.parseDimension(hob.width)     * factor;
        if (sinkCx && sinkW && hobCx && hobW) {
          const gap = Math.abs(sinkCx - hobCx) - (sinkW + hobW) / 2;
          if (gap < MIN_GAP_MM) {
            const msg = `Vask og kogeplade overlapper eller er for tæt (min. ${this.formatLimit(MIN_GAP_MM)} mellem dem).`;
            ['sink', 'hob'].forEach(type => {
              const el = this.querySelector(`[data-cutout-error="${type}"]`);
              if (el) { el.textContent = msg; el.style.display = ''; }
            });
            valid = false;
          }
        }
      }

      return valid;
    }

    // ─── Surface ──────────────────────────────────────────────────

    renderSurfaceOptions() {
      const select = this.querySelector('#surface-treatment');
      if (!select || !this.config.surfaces) return;

      // P3 #18: Ensure 'none' option exists even if config doesn't start with it
      const surfaces = this.config.surfaces;
      const hasNone = surfaces.some(s => s.handle === 'none');
      let options = '';
      if (!hasNone) {
        options += '<option value="none">Ingen behandling</option>';
      }
      options += surfaces.map(s =>
        `<option value="${s.handle}">${s.name}</option>`
      ).join('');
      select.innerHTML = options;

      // Set initial state to match select value
      this.state.surface = select.value;

      select.addEventListener('change', () => {
        this.state.surface = select.value;
        this.updatePrice();
      });
    }

    bindSurface() {
      // P4: Client feedback removed the 1×/2× treatment toggle — always implicit 1 treatment.
      // Kept as a no-op so init() wiring stays consistent.
    }

    // ─── Pricing Engine ───────────────────────────────────────────

    /** Aug 2026: the sink and hob cutout price labels used to be hardcoded strings in the
     *  Liquid. They differ per main material, so they are written here, incl. VAT to match
     *  every other customer-facing figure. A genuine 0 renders as "Inkluderet" rather than
     *  "0,00 DKK", because stone sink cutouts are free only because the cost sits in the
     *  sink product itself. */
    updateCutoutPriceLabels() {
      const mm = this.getMainMaterial();
      const prices = {
        sink: mm.sink_cutout_price ?? this.config.sink_cutout_price ?? 1999.20,
        hob: mm.hob_cutout_price ?? this.config.hob_cutout_price ?? 639.20
      };
      Object.entries(prices).forEach(([key, exVat]) => {
        const el = this.querySelector(`[data-cutout-price="${key}"]`);
        if (!el) return;
        el.textContent = exVat > 0 ? `(${this.formatDKK(this.inclVat(exVat))})` : '(Inkluderet)';
      });
    }

    /** Phase 6: hide the whole surface treatment block for a main material that does not
     *  allow it, and make sure nothing is still selected underneath. */
    updateSurfaceVisibility() {
      const allowed = this.getMainMaterial().allows_surface_treatment !== false;
      const section = this.querySelector('#calculator-surface-section');
      if (section) section.style.display = allowed ? '' : 'none';
      if (!allowed) {
        this.state.surface = 'none';
        const sel = this.querySelector('#surface-treatment');
        if (sel) sel.value = 'none';
      }
    }

    /** Thomas 25 Aug: the sink help text opens with "Ved stenbordplader", so it is shown
     *  only when stone is the main material. The hob text has no such marker and applies
     *  to both, so it carries no data-main-material and is left alone here. */
    updateCutoutHelp() {
      this.querySelectorAll('[data-cutout-help][data-main-material]').forEach(el => {
        el.style.display = el.dataset.mainMaterial === this.state.main_material ? '' : 'none';
      });
    }

    updatePrice() {
      this.updateCutoutPriceLabels();
      this.updateCutoutHelp();
      this.updateSurfaceVisibility();
      const prices = this.calculatePrices();
      this.log('prices', prices);

      // P4: Client feedback — all breakdown rows show incl. 25% VAT.
      // Only the explicit "Subtotal (ekskl. moms)" row stays ex-VAT.
      const vatMultiplier = 1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT);
      this.setPrice('material', prices.material * vatMultiplier);
      this.setPrice('edges', prices.edges * vatMultiplier);
      this.setPrice('corners', prices.corners * vatMultiplier);
      this.setPrice('cutouts', prices.cutouts * vatMultiplier);
      this.setPrice('surface', prices.surface * vatMultiplier);
      this.setPrice('sink', prices.sink * vatMultiplier);
      this.setPrice('assembly', prices.assembly * vatMultiplier);
      this.setPrice('service_package', prices.service_package * vatMultiplier);
      this.setPrice('subtotal', prices.subtotal); // ekskl. moms — leave ex-VAT
      this.setPrice('total', prices.total);       // already incl. VAT in calculatePrices()

      // Show/hide zero rows
      ['edges', 'corners', 'cutouts', 'surface', 'sink', 'assembly', 'service_package'].forEach(key => {
        const row = this.querySelector(`[data-price-row="${key}"]`);
        if (row) row.style.display = prices[key] > 0 ? '' : 'none';
      });

      // Show download button when we have a price
      const dlBtn = this.querySelector('.calculator__download-btn');
      if (dlBtn) dlBtn.style.display = prices.subtotal > 0 ? '' : 'none';
    }

    calculatePrices() {
      const result = { material: 0, edges: 0, corners: 0, cutouts: 0, surface: 0, sink: 0, assembly: 0, service_package: 0, subtotal: 0, total: 0 };

      if (!this.state.material) return result;

      // Aug 2026: corner, cutout and assembly prices are per main material, not global.
      // Every lookup below uses `??` rather than `||` because stone has REAL zeros that
      // must survive: sink cutout 0 (included in the stone sink price) and assembly fee 0.
      // With `||` those would fall through to wood's 1999.20 and 716.00 and overcharge
      // every stone order, which is exactly the kind of bug that never announces itself.
      const mm = this.getMainMaterial();

      // Defensive lookup — warns when state references a material that isn't in
      // the metaobject-driven config (would silently zero out the order otherwise).
      const mat = this.config.materials.find(m => m.handle === this.state.material);
      if (!mat) {
        console.warn(`[BoardCalculator] No material with handle "${this.state.material}" in config — zero price returned. Available handles:`, this.config.materials?.map(m => m.handle));
        return result;
      }

      const priceKey = `price_${this.state.thickness}mm`;
      const pricePerM2 = mat[priceKey] || 0;
      if (!pricePerM2) {
        console.warn(`[BoardCalculator] Material "${this.state.material}" has no ${priceKey} — zero price returned. Material entry:`, mat);
      }

      // Material cost: sum of area per board (dimensions in mm → convert to m²)
      let totalAreaM2 = 0;
      this.state.boards.forEach(board => {
        const { length, width } = this.getDimensionMM(board);
        if (this.state.formation === 'round') {
          const r = length / 2;
          totalAreaM2 += (Math.PI * r * r) / 1000000;
        } else {
          totalAreaM2 += (length * width) / 1000000;
        }
      });
      // Followup #3 (24 June 2026): round boards cost more to produce than the
      // raw m² suggests (waste from cutting a circle out of a rectangular blank
      // + extra setup). Per Thomas's spec the material m² for a round formation
      // is multiplied by (1 + round_addition_pct / 100). Default 27 %; editable
      // via the `round_addition_pct` field on the `calculator_globals`
      // metaobject. `??` (not `||`) so an explicit 0 from Thomas disables the
      // markup cleanly without falling through to the default.
      if (this.state.formation === 'round') {
        const pct = (this.config.round_addition_pct ?? 27) / 100;
        totalAreaM2 *= 1 + pct;
      }
      result.material = totalAreaM2 * pricePerM2;

      // Edges
      const edgeList = this.config.edges || [];
      this.state.edges.forEach(boardEdges => {
        if (this.state.formation === 'round') {
          const edge = edgeList.find(e => e.handle === boardEdges.A);
          if (edge) result.edges += edge.price;
        } else {
          ['A', 'B', 'C', 'D'].forEach(side => {
            const edge = edgeList.find(e => e.handle === boardEdges[side]);
            if (edge) result.edges += edge.price;
          });
        }
      });

      // Corners
      Object.values(this.state.corners).forEach(corner => {
        if (corner.type === 'square') {
          result.corners += mm.corner_price ?? this.config.corner_price ?? BoardCalculator.CORNER_PRICE_DEFAULT;
        }
      });

      // Cutouts
      if (this.state.cutouts.sink.enabled) result.cutouts += mm.sink_cutout_price ?? this.config.sink_cutout_price ?? 1999.20;
      if (this.state.cutouts.hob.enabled) result.cutouts += mm.hob_cutout_price ?? this.config.hob_cutout_price ?? 639.20;

      // Surface treatment — P4: client feedback removed treatment count; always one pass implicitly
      // Stone gets no surface treatment. setMainMaterial() already forces 'none', this is a
      // second guard so a stale state can never bill an oil treatment on a stone board.
      if (this.state.surface !== 'none' && mm.allows_surface_treatment !== false) {
        const surf = this.config.surfaces.find(s => s.handle === this.state.surface);
        if (surf) {
          result.surface = (surf.startup_fee || 0) + (totalAreaM2 * (surf.sqm_price || 0));
        }
      }

      // Assembly
      const boardCount = this.formationBoardCounts[this.state.formation];
      if (boardCount > 1) {
        result.assembly = (boardCount - 1) * (mm.assembly_fee ?? this.config.assembly_fee ?? BoardCalculator.ASSEMBLY_FEE_DEFAULT);
      }

      // Sink product — Shopify storefront serves Danish customer-facing prices INCL VAT.
      // Internally we store ex-VAT in result.sink so the same `× (1 + vat_rate)` pipeline
      // that handles every other row produces the original incl-VAT figure on display.
      const sinkInclVat = (this.state.cutouts.sink.enabled && this.state.cutouts.sink.product_price) || 0;
      if (sinkInclVat > 0) {
        result.sink = sinkInclVat / (1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT));
      }

      // Service package — like the sink, this is a real Shopify product whose storefront
      // price already includes VAT, so it is stored ex-VAT here and the shared
      // `× (1 + vat_rate)` display pipeline puts it back.
      const pkgVat = 1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT);
      result.service_package = this.getSelectedPackages()
        .reduce((sum, pkg) => sum + ((pkg.price_cents || 0) / 100) / pkgVat, 0);

      result.subtotal = result.material + result.edges + result.corners + result.cutouts + result.surface + result.sink + result.assembly + result.service_package;
      result.total = result.subtotal * (1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT));

      return result;
    }

    setPrice(key, value) {
      const el = this.querySelector(`[data-price="${key}"]`);
      if (el) el.textContent = this.formatDKK(value);
    }

    formatDKK(value) {
      return value.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DKK';
    }

    inclVat(value) {
      return value * (1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT));
    }

    // ─── SVG Preview ──────────────────────────────────────────────

    updateSVG() {
      const svg = this.querySelector('#calculator-svg');
      if (!svg) return;

      const formation = this.state.formation;
      const boards = this.state.boards;

      if (formation === 'round') {
        this.renderRoundSVG(svg, boards[0]);
      } else if (formation === 'single_rect') {
        this.renderRectSVG(svg, boards);
      } else {
        this.renderMultiBoardSVG(svg, boards, formation);
      }
    }

    renderRoundSVG(svg, board) {
      const dim = this.getDimensionMM(board || { length: '', width: '' });
      const r = Math.max(dim.length / 2, 50);
      const scale = 150 / Math.max(r, 1);
      const cx = 250, cy = 200;
      const sr = Math.min(r * scale, 180);

      let html = `<circle cx="${cx}" cy="${cy}" r="${sr}" fill="none" stroke="var(--color-body-text)" stroke-width="1.5"/>`;

      // Grain lines — horizontal for round boards
      for (let i = -sr + 15; i < sr; i += 18) {
        const halfChord = Math.sqrt(sr * sr - i * i);
        html += `<line x1="${cx - halfChord}" y1="${cy + i}" x2="${cx + halfChord}" y2="${cy + i}" stroke="var(--color-body-text)" stroke-width="0.5" opacity="0.15"/>`;
      }

      // Dimension label
      if (dim.length > 0) {
        const label = this.state.unit === 'cm' ? `⌀ ${(dim.length / 10).toFixed(1)} cm` : `⌀ ${dim.length} mm`;
        html += `<text x="${cx}" y="${cy + sr + 25}" text-anchor="middle" fill="var(--color-body-text)" font-size="12">${label}</text>`;
      }

      svg.innerHTML = html;
    }

    renderRectSVG(svg, boards) {
      const board = boards[0] || { length: '', width: '' };
      const dim = this.getDimensionMM(board);
      const l = Math.max(dim.length, 200);
      const w = Math.max(dim.width, 100);

      const maxW = 400, maxH = 300;
      const scale = Math.min(maxW / l, maxH / w, 1.5);
      const sw = l * scale, sh = w * scale;
      const sx = (500 - sw) / 2, sy = (400 - sh) / 2;

      const labels = this.formationLabels.single_rect[1];
      let html = this.drawBoard(sx, sy, sw, sh, dim, 'var(--color-body-text)', false, {
        labels,
        boardNumber: 1,
        formation: 'single_rect'
      });

      // Cutouts — single-board formation: board index 1, A is at the bottom by default
      html += this.drawCutouts(sx, sy, sw, sh, l, w, 1, false, labels);

      svg.innerHTML = html;
    }

    renderMultiBoardSVG(svg, boards, formation) {
      // P5 (23 April 2026): geometry derived from formation icons. Each board's pixel
      // size scales WITH its actual length/width inputs (fixes the 'boards 2/3 don't refresh' bug).
      const placements = this.computeFormationPlacements(formation, boards);
      const html = placements.map(p =>
        this.drawBoard(p.x, p.y, p.w, p.h, p.dim, 'var(--color-body-text)', p.vertical, {
          labels: this.formationLabels[formation]?.[p.num] || {},
          boardNumber: p.num,
          formation: formation
        })
        + this.drawCutouts(p.x, p.y, p.w, p.h, p.lengthMm, p.widthMm, p.num, p.vertical, this.formationLabels[formation]?.[p.num])
      ).join('');
      svg.innerHTML = html;
    }

    /**
     * P5 (23 April 2026): single source of truth for multi-board geometry.
     * Returns an array of board placements: { num, x, y, w, h, vertical, dim, lengthMm, widthMm }.
     * `num` is the board NUMBER (1, 2, 3) per the formation icon — this drives label "Plade N"
     * and is what the user sees in the dimension form headings.
     * Each piece scales with its own dimensions so editing board 2's inputs refreshes board 2's box.
     */
    computeFormationPlacements(formation, boards) {
      const dims = boards.map(b => this.getDimensionMM(b || { length: '', width: '' }));
      // Provide visual minimums so an empty form still draws a recognisable shape.
      const D = dims.map(d => ({ length: Math.max(d.length, 200), width: Math.max(d.width, 80) }));
      // Canvas: 500x400 viewBox, leave 50px margin on each side
      const VBW = 400, VBH = 320, OFFSET_X = 50, OFFSET_Y = 40;

      switch (formation) {
        case 'single_rect': {
          const l = D[0].length, w = D[0].width;
          const s = Math.min(VBW / l, VBH / w, 1.5);
          const sw = l * s, sh = w * s;
          return [{ num: 1, x: (500 - sw) / 2, y: (400 - sh) / 2, w: sw, h: sh, vertical: false, dim: dims[0], lengthMm: l, widthMm: w }];
        }
        case 'l_right': {
          // Board 1 = vertical bottom-LEFT leg, Board 2 = horizontal full TOP
          // Board 2 spans the total horizontal footprint; board 1 hangs from bottom-left of board 2.
          const totalL = Math.max(D[0].width, D[1].length); // total horizontal extent
          const totalH = D[1].width + D[0].length; // total vertical extent (board 2 height + board 1 length going down)
          const s = Math.min(VBW / totalL, VBH / totalH, 1);
          const b2w = D[1].length * s, b2h = D[1].width * s;
          const b1w = D[0].width * s, b1h = D[0].length * s;
          // Board 2 at top, board 1 at bottom-left of board 2's left edge
          return [
            { num: 1, x: OFFSET_X,         y: OFFSET_Y + b2h, w: b1w, h: b1h, vertical: true,  dim: dims[0], lengthMm: D[0].length, widthMm: D[0].width },
            { num: 2, x: OFFSET_X,         y: OFFSET_Y,       w: b2w, h: b2h, vertical: false, dim: dims[1], lengthMm: D[1].length, widthMm: D[1].width }
          ];
        }
        case 'l_left': {
          // Board 1 = vertical full-LEFT (tall), Board 2 = horizontal top-RIGHT
          // Board 1 is full height; board 2 is shorter horizontal sitting at top-right of board 1.
          const totalL = D[0].width + D[1].length;
          const totalH = Math.max(D[0].length, D[1].width);
          const s = Math.min(VBW / totalL, VBH / totalH, 1);
          const b1w = D[0].width * s, b1h = D[0].length * s;
          const b2w = D[1].length * s, b2h = D[1].width * s;
          return [
            { num: 1, x: OFFSET_X,         y: OFFSET_Y, w: b1w, h: b1h, vertical: true,  dim: dims[0], lengthMm: D[0].length, widthMm: D[0].width },
            { num: 2, x: OFFSET_X + b1w,   y: OFFSET_Y, w: b2w, h: b2h, vertical: false, dim: dims[1], lengthMm: D[1].length, widthMm: D[1].width }
          ];
        }
        case 'l_top': {
          // Board 1 = horizontal full TOP, Board 2 = vertical bottom-RIGHT
          const totalL = Math.max(D[0].length, D[1].width);
          const totalH = D[0].width + D[1].length;
          const s = Math.min(VBW / totalL, VBH / totalH, 1);
          const b1w = D[0].length * s, b1h = D[0].width * s;
          const b2w = D[1].width * s, b2h = D[1].length * s;
          return [
            { num: 1, x: OFFSET_X,           y: OFFSET_Y,       w: b1w, h: b1h, vertical: false, dim: dims[0], lengthMm: D[0].length, widthMm: D[0].width },
            { num: 2, x: OFFSET_X + b1w - b2w, y: OFFSET_Y + b1h, w: b2w, h: b2h, vertical: true,  dim: dims[1], lengthMm: D[1].length, widthMm: D[1].width }
          ];
        }
        case 'l_bottom': {
          // Round 6 (5 May 2026): board 1 = horizontal TOP-LEFT (partial width),
          //                        board 2 = vertical FULL-RIGHT (full height of the formation,
          //                        taller than board 1). Board 1's right edge butts against
          //                        board 2's left edge for the upper portion only.
          const totalL = D[0].length + D[1].width;       // b1 horizontal + b2 horizontal
          const totalH = Math.max(D[0].width, D[1].length); // taller of the two pieces
          const s = Math.min(VBW / totalL, VBH / totalH, 1);
          const b1w = D[0].length * s, b1h = D[0].width * s;
          const b2w = D[1].width * s,  b2h = D[1].length * s;
          return [
            { num: 1, x: OFFSET_X,       y: OFFSET_Y, w: b1w, h: b1h, vertical: false, dim: dims[0], lengthMm: D[0].length, widthMm: D[0].width },
            { num: 2, x: OFFSET_X + b1w, y: OFFSET_Y, w: b2w, h: b2h, vertical: true,  dim: dims[1], lengthMm: D[1].length, widthMm: D[1].width }
          ];
        }
        case 'u_sides': {
          // Board 1 = vertical bottom-LEFT, Board 2 = horizontal full TOP, Board 3 = vertical bottom-RIGHT
          // Board 2 spans full horizontal width; legs hang from below at left + right.
          const legL = Math.max(D[0].length, D[2].length);
          const totalL = D[1].length;
          const totalH = D[1].width + legL;
          const s = Math.min(VBW / totalL, VBH / totalH, 1);
          const b1w = D[0].width * s, b1h = D[0].length * s;
          const b2w = D[1].length * s, b2h = D[1].width * s;
          const b3w = D[2].width * s, b3h = D[2].length * s;
          return [
            { num: 1, x: OFFSET_X,                 y: OFFSET_Y + b2h, w: b1w, h: b1h, vertical: true,  dim: dims[0], lengthMm: D[0].length, widthMm: D[0].width },
            { num: 2, x: OFFSET_X,                 y: OFFSET_Y,       w: b2w, h: b2h, vertical: false, dim: dims[1], lengthMm: D[1].length, widthMm: D[1].width },
            { num: 3, x: OFFSET_X + b2w - b3w,     y: OFFSET_Y + b2h, w: b3w, h: b3h, vertical: true,  dim: dims[2], lengthMm: D[2].length, widthMm: D[2].width }
          ];
        }
        case 'u_top_bottom': {
          // Board 1 = vertical full-LEFT, Board 2 = horizontal top-CENTER, Board 3 = vertical full-RIGHT
          // Boards 1 and 3 are full height; board 2 spans only the gap between them.
          const totalH = Math.max(D[0].length, D[2].length);
          const totalL = D[0].width + D[1].length + D[2].width;
          const s = Math.min(VBW / totalL, VBH / totalH, 1);
          const b1w = D[0].width * s, b1h = D[0].length * s;
          const b2w = D[1].length * s, b2h = D[1].width * s;
          const b3w = D[2].width * s, b3h = D[2].length * s;
          return [
            { num: 1, x: OFFSET_X,                 y: OFFSET_Y, w: b1w, h: b1h, vertical: true,  dim: dims[0], lengthMm: D[0].length, widthMm: D[0].width },
            { num: 2, x: OFFSET_X + b1w,           y: OFFSET_Y, w: b2w, h: b2h, vertical: false, dim: dims[1], lengthMm: D[1].length, widthMm: D[1].width },
            { num: 3, x: OFFSET_X + b1w + b2w,     y: OFFSET_Y, w: b3w, h: b3h, vertical: true,  dim: dims[2], lengthMm: D[2].length, widthMm: D[2].width }
          ];
        }
        default:
          return [];
      }
    }

    /**
     * Draw a board rectangle with grain lines, INSIDE-edge ABCD labels, board number,
     * corner cutout notches, and active-corner highlight.
     * @param {object} opts - { labels: {top|right|bottom|left: 'A'|'B'|'C'|'D'}, boardNumber, formation }
     */
    drawBoard(x, y, w, h, dim, color, vertical = false, opts = {}) {
      const labels = opts.labels || {};
      const boardNum = opts.boardNumber || 1;
      const formation = opts.formation || this.state.formation;
      const accent = 'var(--calculator-accent, #BC6B48)';

      // P5 #2 (23 April): build a notched polygon path so square corners visibly
      // cut into the board outline. Falls back to a plain rect when no corners are set.
      // Round 3 (1 May): pass per-board `labels` so corner-key resolution stays
      // consistent with the focus-highlight (which already uses per-board labels).
      let html = '';
      const cornerCuts = this.collectCornerCuts(boardNum, x, y, w, h, vertical, labels);
      if (cornerCuts.length === 0) {
        html += `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none" stroke="${color}" stroke-width="1.5" rx="1"/>`;
      } else {
        html += `<polygon points="${this.buildNotchedPath(x, y, w, h, cornerCuts)}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linejoin="round"/>`;
      }

      // Grain direction lines — lines run IN the grain direction
      if (vertical) {
        for (let gx = x + 8; gx < x + w - 5; gx += 12) {
          html += `<line x1="${gx}" y1="${y + 3}" x2="${gx}" y2="${y + h - 3}" stroke="${color}" stroke-width="0.5" opacity="0.15"/>`;
        }
      } else {
        for (let gy = y + 8; gy < y + h - 5; gy += 12) {
          html += `<line x1="${x + 3}" y1="${gy}" x2="${x + w - 3}" y2="${gy}" stroke="${color}" stroke-width="0.5" opacity="0.15"/>`;
        }
      }

      // P5 #5 (23 April): big semi-transparent board number in the centre.
      const showBoardNumber = opts.showBoardNumber !== false;
      if (showBoardNumber && Math.min(w, h) > 30) {
        const numFontSize = Math.max(20, Math.min(40, Math.min(w, h) * 0.4));
        html += `<text x="${x + w / 2}" y="${y + h / 2 + numFontSize * 0.35}" text-anchor="middle" fill="${accent}" opacity="0.18" font-size="${numFontSize}" font-weight="800" font-family="-apple-system, sans-serif">${boardNum}</text>`;
      }

      // P5 #1 (23 April): ABCD labels INSIDE the rect, near each edge.
      // labels = { top:'?', right:'?', bottom:'?', left:'?' } — only present sides render.
      const lblFont = 11;
      const inset = 8;
      if (labels.top)    html += `<text x="${x + w / 2}" y="${y + inset + lblFont}" text-anchor="middle" fill="${color}" font-size="${lblFont}" font-weight="700">${labels.top}</text>`;
      if (labels.bottom) html += `<text x="${x + w / 2}" y="${y + h - inset}" text-anchor="middle" fill="${color}" font-size="${lblFont}" font-weight="700">${labels.bottom}</text>`;
      if (labels.left)   html += `<text x="${x + inset + 2}" y="${y + h / 2 + lblFont * 0.35}" text-anchor="start" fill="${color}" font-size="${lblFont}" font-weight="700">${labels.left}</text>`;
      if (labels.right)  html += `<text x="${x + w - inset - 2}" y="${y + h / 2 + lblFont * 0.35}" text-anchor="end" fill="${color}" font-size="${lblFont}" font-weight="700">${labels.right}</text>`;

      // P5 #2 (23 April): active-corner highlight ring while the user is editing a corner field.
      if (this.activeCorner) {
        const [actBoard, actCorner] = this.activeCorner.split('_');
        if (parseInt(actBoard, 10) === boardNum) {
          const cp = this.cornerLabelToPoint(actCorner, x, y, w, h, labels);
          if (cp) {
            html += `<circle cx="${cp.x}" cy="${cp.y}" r="9" fill="${accent}" fill-opacity="0.25" stroke="${accent}" stroke-width="2"><animate attributeName="r" values="6;12;6" dur="1.4s" repeatCount="indefinite"/></circle>`;
          }
        }
      }

      // Dimension labels (length below the rect, width to the right)
      if (dim && dim.length > 0) {
        const unit = this.state.unit;
        const lLabel = unit === 'cm' ? `${(dim.length / 10).toFixed(1)}` : `${dim.length}`;
        const wLabel = dim.width > 0 ? (unit === 'cm' ? `${(dim.width / 10).toFixed(1)}` : `${dim.width}`) : '';

        // For a vertical board, "length" runs vertically — show length next to the side, not below.
        if (vertical) {
          html += `<text x="${x + w + 4}" y="${y + h / 2 + 4}" text-anchor="start" fill="${color}" font-size="10" opacity="0.7">${lLabel} ${unit}</text>`;
          if (wLabel) html += `<text x="${x + w / 2}" y="${y + h + 14}" text-anchor="middle" fill="${color}" font-size="10" opacity="0.7">${wLabel} ${unit}</text>`;
        } else {
          html += `<text x="${x + w / 2}" y="${y + h + 14}" text-anchor="middle" fill="${color}" font-size="10" opacity="0.7">${lLabel} ${unit}</text>`;
          if (wLabel) html += `<text x="${x + w + 4}" y="${y + h / 2 + 4}" text-anchor="start" fill="${color}" font-size="10" opacity="0.7">${wLabel} ${unit}</text>`;
        }
      }

      return html;
    }

    /**
     * Round 3 (1 May): given a corner key like 'AB' and the per-board labels map
     * `{ top:'A'|'B'|... , right:..., bottom:..., left:... }`, return one of
     * 'tl' | 'tr' | 'bl' | 'br' identifying the physical SVG corner. Returns
     * null if the key references letters that aren't in the labels map.
     */
    resolveCornerPos(cornerKey, labels) {
      if (!labels) return null;
      const sideOf = {};
      Object.entries(labels).forEach(([side, letter]) => { sideOf[letter] = side; });
      const [a, b] = cornerKey.split('');
      const sideA = sideOf[a], sideB = sideOf[b];
      if (!sideA || !sideB) return null;
      const pair = [sideA, sideB].sort().join('-');
      return ({ 'bottom-left': 'bl', 'bottom-right': 'br', 'left-top': 'tl', 'right-top': 'tr' })[pair] || null;
    }

    /** P5 #2: collect square-corner cuts for a given board, returned as { corner: 'AB'|..., pos: 'tl'|..., lengthPx, depthPx }. */
    collectCornerCuts(boardNum, bx, by, bw, bh, vertical, labels) {
      const cuts = [];
      const factor = this.state.unit === 'cm' ? 10 : 1;
      const boardState = this.state.boards[boardNum - 1];
      if (!boardState) return cuts;
      const dimMm = this.getDimensionMM(boardState);
      // pixels-per-mm: bw maps to the SVG-horizontal-axis (= L for horizontal boards, W for vertical).
      const pxPerMmX = bw / (vertical ? (dimMm.width  || 1) : (dimMm.length || 1));
      const pxPerMmY = bh / (vertical ? (dimMm.length || 1) : (dimMm.width  || 1));

      Object.entries(this.state.corners).forEach(([key, c]) => {
        if (c.type !== 'square') return;
        const [bn, corner] = key.split('_');
        if (parseInt(bn, 10) !== boardNum) return;
        const cl = this.parseDimension(c.length) * factor;
        const cd = this.parseDimension(c.depth) * factor;
        if (!cl || !cd) return;
        const pos = this.resolveCornerPos(corner, labels);
        if (!pos) return;
        // `lengthPx` and `depthPx` are always (horizontal, vertical) SVG pixel
        // extents — buildNotchedPath consumes them that way for every corner.
        // The user's `Længde` runs along the wood grain — horizontal for
        // horizontal boards, vertical for rotated (vertical) boards — so when
        // the board is vertical we swap cl ↔ cd before scaling. Same pattern
        // drawCutouts uses for sink/hob cutouts (1879-1898 swap cw/cd when
        // aSide is left/right). Without this swap, every corner cutout on a
        // vertical-grain board renders with its dimensions rotated 90°.
        const horizMm = vertical ? cd : cl;
        const vertMm  = vertical ? cl : cd;
        cuts.push({ corner, pos, lengthPx: horizMm * pxPerMmX, depthPx: vertMm * pxPerMmY });
      });
      return cuts;
    }

    /** P5 #2: build a polygon-points string for a board with rectangular corner notches. */
    buildNotchedPath(x, y, w, h, cuts) {
      // Round 3 (1 May): physical-corner position now comes from collectCornerCuts (per-board aware).
      const notches = { tl: null, tr: null, bl: null, br: null };
      cuts.forEach(c => { if (c.pos) notches[c.pos] = c; });

      const points = [];
      // Walk the rectangle clockwise from top-left: TL → TR → BR → BL → close.
      // At each corner, when a notch is present, dip inward through 3 vertices.
      // The 3 vertices must be pushed in the same CW order as the surrounding walk
      // — i.e. encountered while traversing the polygon boundary clockwise from
      // the previous corner toward the next one. The TL block was previously
      // reversed (interior dip first instead of last), which produced a polygon
      // that crossed itself diagonally — the visual "BC notch on the top board
      // looks wrong" symptom Thomas reported on 5 May (BC resolves to TL via
      // the per-board labels for any horizontal "top" board).
      const tl = notches.tl, tr = notches.tr, br = notches.br, bl = notches.bl;
      // Top-left — incoming from BL (left edge going up) → outgoing toward TR (top edge going right).
      if (tl) {
        points.push([x, y + tl.depthPx]);              // left edge stops at notch
        points.push([x + tl.lengthPx, y + tl.depthPx]); // notch inner corner
        points.push([x + tl.lengthPx, y]);              // back on top edge after notch
      } else {
        points.push([x, y]);
      }
      // Top-right
      if (tr) {
        points.push([x + w - tr.lengthPx, y]);
        points.push([x + w - tr.lengthPx, y + tr.depthPx]);
        points.push([x + w, y + tr.depthPx]);
      } else {
        points.push([x + w, y]);
      }
      // Bottom-right
      if (br) {
        points.push([x + w, y + h - br.depthPx]);
        points.push([x + w - br.lengthPx, y + h - br.depthPx]);
        points.push([x + w - br.lengthPx, y + h]);
      } else {
        points.push([x + w, y + h]);
      }
      // Bottom-left
      if (bl) {
        points.push([x + bl.lengthPx, y + h]);
        points.push([x + bl.lengthPx, y + h - bl.depthPx]);
        points.push([x, y + h - bl.depthPx]);
      } else {
        points.push([x, y + h]);
      }
      return points.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
    }

    /** P5 #2: given a corner key like 'AB', resolve which (x,y) point on the rect to highlight. */
    cornerLabelToPoint(cornerKey, x, y, w, h, labels) {
      const pos = this.resolveCornerPos(cornerKey, labels);
      if (!pos) return null;
      return ({
        tl: { x: x,     y: y },
        tr: { x: x + w, y: y },
        bl: { x: x,     y: y + h },
        br: { x: x + w, y: y + h }
      })[pos] || null;
    }

    /**
     * P5 #3 (23 April): cutouts now placed 6 cm from the FRONT (A side), not the back (C).
     * Board orientation labels (which side is A) come from formationLabels — the renderer
     * draws the cutout at the side where label "A" sits.
     */
    drawCutouts(bx, by, bw, bh, boardLmm, boardWmm, boardIndex = 1, vertical = false, labels = null) {
      let html = '';
      const aSide = labels ? Object.keys(labels).find(k => labels[k] === 'A') : 'bottom';

      ['sink', 'hob'].forEach(type => {
        const cutout = this.state.cutouts[type];
        if (!cutout.enabled) return;
        if ((cutout.board_index || 1) !== boardIndex) return;

        const factor = this.state.unit === 'cm' ? 10 : 1;
        const cw = this.parseDimension(cutout.width) * factor;
        const cd = this.parseDimension(cutout.depth) * factor;
        const cx_mm = this.parseDimension(cutout.center_x) * factor;
        if (!cw || !cd || !cx_mm) return;

        // Round 3 (1 May): scale factors must respect board orientation.
        // For horizontal boards: SVG-x maps to length-axis-mm, SVG-y to width-axis-mm.
        // For vertical boards:   SVG-x maps to width-axis-mm,  SVG-y to length-axis-mm.
        const scaleX = vertical ? (bw / boardWmm) : (bw / boardLmm);
        const scaleY = vertical ? (bh / boardLmm) : (bh / boardWmm);
        const yOffsetMm = BoardCalculator.CUTOUT_Y_MM; // 60mm from front (A) edge

        // Round 3 (1 May): cx_mm is uniformly measured from the AB-corner along
        // side A in the clockwise direction (matches the spec). AB-corner per A-side:
        //   A=bottom → AB at bottom-LEFT  → cx increases LEFT-to-RIGHT
        //   A=right  → AB at bottom-RIGHT → cx increases BOTTOM-to-TOP
        //   A=top    → AB at top-RIGHT    → cx increases RIGHT-to-LEFT
        //   A=left   → AB at top-LEFT     → cx increases TOP-to-BOTTOM
        let rx, ry, rw, rh;
        if (aSide === 'bottom') {
          rx = bx + (cx_mm - cw / 2) * scaleX;
          ry = by + bh - (yOffsetMm + cd) * scaleY;
          rw = cw * scaleX;
          rh = cd * scaleY;
        } else if (aSide === 'top') {
          rx = bx + bw - (cx_mm + cw / 2) * scaleX; // FIX: cx from RIGHT (AB-corner)
          ry = by + yOffsetMm * scaleY;
          rw = cw * scaleX;
          rh = cd * scaleY;
        } else if (aSide === 'left') {
          rx = bx + yOffsetMm * scaleX;
          ry = by + (cx_mm - cw / 2) * scaleY; // cx from TOP (AB-corner) ✓
          rw = cd * scaleX;
          rh = cw * scaleY;
        } else { // right
          rx = bx + bw - (yOffsetMm + cd) * scaleX;
          ry = by + bh - (cx_mm + cw / 2) * scaleY; // FIX: cx from BOTTOM (AB-corner)
          rw = cd * scaleX;
          rh = cw * scaleY;
        }

        const color = type === 'sink' ? '#2563eb' : '#dc2626';
        html += `<rect x="${rx}" y="${ry}" width="${rw}" height="${rh}" fill="${color}" fill-opacity="0.15" stroke="${color}" stroke-width="1" stroke-dasharray="4 2" rx="2"/>`;
        html += `<text x="${rx + rw / 2}" y="${ry + rh / 2 + 4}" text-anchor="middle" fill="${color}" font-size="9">${type === 'sink' ? 'Vask' : 'Kogeplade'}</text>`;
      });

      return html;
    }

    // ─── Cart Integration ─────────────────────────────────────────

    async addToCart() {
      if (!this.validateCurrentStep()) return;
      if (!this.validateCutouts()) return;

      const prices = this.calculatePrices();
      if (prices.subtotal <= 0) {
        alert('Konfigurer venligst din plade før du tilføjer til kurv');
        return;
      }

      const props = this.buildLineItemProperties(prices);

      const form = this.querySelector('#calculator-product-form');
      const variantId = form?.querySelector('input[name="id"]')?.value;
      if (!variantId) return;

      const cartBtn = this.querySelector('.calculator__nav-btn--cart');
      if (cartBtn) {
        cartBtn.disabled = true;
        cartBtn.textContent = 'Tilføjer...';
      }

      // Generate preview thumbnail at low resolution (small payload, ~5-15KB)
      const previewDataUrl = await this.renderSvgToPng(0.6);

      // Round 6 follow-up (6 May 2026): upload a higher-res PNG of the drawing to
      // Shopify Files via the Netlify backend. The returned CDN URL is short and
      // works in admin, order emails, and CSV exports — unlike the inline data URL
      // which was opaque text in the admin and blocked by Outlook in emails.
      // If the upload fails for any reason, fall back to the inline SVG so the
      // drawing data is never lost.
      const drawingPng = await this.renderSvgToPng(1.5);
      const drawingUrl = drawingPng ? await this.uploadDrawing(drawingPng) : null;
      if (drawingUrl) {
        props['_drawing_url'] = drawingUrl;
      } else {
        const fallback = this.buildDrawingSvgDataUrl();
        if (fallback) props['_drawing_svg'] = fallback;
      }

      // Check whether the theme uses a cart drawer or cart page
      const cartDrawer = document.querySelector('cart-drawer');
      const cartType = cartDrawer?.dataset?.cartType || 'page';

      // When using the drawer, request sections from /cart/add.js to refresh the drawer
      // (the drawer's renderContents method consumes the sections data)
      const sectionsToRender = cartType === 'drawer' && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(s => s.id).join(',')
        : null;

      try {
        // P5 #4 (23 April): build a multi-item cart payload — the calculator board PLUS
        // the chosen sink product (when sink cutout is enabled and a sink was picked).
        const items = [{
          id: parseInt(variantId),
          quantity: 1,
          properties: props
        }];
        const sink = this.state.cutouts.sink;
        if (sink.enabled && sink.variant_id) {
          items.push({
            id: sink.variant_id,
            quantity: 1
          });
        }
        // Phase 8: the chosen measurement/assembly package rides along as its own cart
        // line, same pattern as the sink. One per order, priced by Shopify.
        this.getSelectedPackages().forEach(pkg => {
          if (pkg.variant_id) items.push({ id: pkg.variant_id, quantity: 1 });
        });

        const body = { items };
        if (sectionsToRender) {
          body.sections = sectionsToRender;
          body.sections_url = window.location.pathname;
        }

        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const response = await r.json();
        if (response.status) {
          alert(response.description || 'Der opstod en fejl');
          return;
        }
        this.log('Cart add success', response);

        // Bulk-add returns { items: [...] } whereas single-add returns the item directly.
        // Find the calculator board item in the response so we can save the preview keyed by its line key.
        const responseItems = response.items || (response.key ? [response] : []);
        const boardItem = responseItems.find(it => it.properties && (
          Array.isArray(it.properties)
            ? it.properties.some(p => p.name === '_calculated_price')
            : '_calculated_price' in it.properties
        )) || responseItems[0];

        if (previewDataUrl && boardItem?.key) {
          try {
            localStorage.setItem('mifa_preview_' + boardItem.key, previewDataUrl);
          } catch (e) {
            this.log('localStorage write failed', e);
          }
        }

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'board-calculator', cartData: response });
        }

        if (cartType === 'drawer' && cartDrawer && typeof cartDrawer.renderContents === 'function') {
          // Open the cart drawer with the new content (matches Wonder theme product-form pattern)
          cartDrawer.renderContents(response, true);
        } else {
          // Cart page mode — redirect to /cart
          window.location = window.routes?.cart_url || '/cart';
        }
      } catch (err) {
        console.error('Cart add error:', err);
        alert('Der opstod en fejl ved tilføjelse til kurv. Tjek venligst at produktet er korrekt opsat.');
      } finally {
        if (cartBtn) {
          cartBtn.disabled = false;
          cartBtn.textContent = 'Tilføj til kurv';
        }
      }
    }

    buildLineItemProperties(prices) {
      const mat = this.config.materials.find(m => m.handle === this.state.material);
      const surf = this.config.surfaces.find(s => s.handle === this.state.surface);
      const unit = this.state.unit;

      // Followup #1a (24 June 2026): show the number of fitting connectors
      // (samlingsbeslag) needed for multi-board formations. The client confirmed
      // the convention: 2 boards → 1 connector, 3 boards → 2 connectors.
      // Generalized as boardCount - 1. Single rectangle / round = 0 connectors,
      // and the row is omitted entirely (object spread of {}). The property is
      // inserted between Formation and Materiale so it shows up where Thomas
      // marked it in the order-confirmation mock.
      const connectorCount = Math.max(0, (this.formationBoardCounts[this.state.formation] || 1) - 1);

      const props = {
        'Formation': this.getFormationLabel(),
        ...(connectorCount > 0 ? { 'Samlingsbeslag': `${connectorCount} stk.` } : {}),
        // 10.2 (Thomas, 24 Aug): the main material on the order. He said it was not needed
        // because the material name already identifies it, but nice to have if small. It is,
        // and it pairs naturally with the line below: "Hovedmateriale: Sten / Materiale: Dekton Arga".
        'Hovedmateriale': this.getMainMaterial().name || '',
        'Materiale': mat?.name || '',
        'Tykkelse': `${this.state.thickness}mm`,
        '_main_material': this.state.main_material,
        '_formation': this.state.formation,
        '_material': this.state.material,
        '_thickness': this.state.thickness,
        '_unit': this.state.unit,
        // Round 6 follow-up (7 May 2026): cart line price for the calculator board
        // must NOT include the sink product price — the sink is added as its own
        // cart line item, so including it here would double-charge the customer.
        // The calculator's preview panel still shows the combined total.
        // Board line only. The sink and the service package are separate cart lines that
        // Shopify prices itself, so both must come off this figure or the draft order
        // charges each of them twice.
        '_calculated_price': Math.round((prices.total
          - (prices.sink || 0) * (1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT))
          - (prices.service_package || 0) * (1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT))) * 100)
      };

      // Per-board dimensions
      // Followup #1b (24 June 2026): split rectangular board dimensions across
      // two properties (Plade N længde mål / Plade N bredde mål) so the
      // Business Central integration can map each axis to its own field. Round
      // boards keep the single "Plade N diameter" line.
      this.state.boards.forEach((board, i) => {
        const n = i + 1;
        const dim = this.getDimensionMM(board);
        if (this.state.formation === 'round') {
          props[`Plade ${n} diameter`] = `${board.length} ${unit}`;
        } else {
          props[`Plade ${n} længde mål`] = `${board.length} ${unit}`;
          props[`Plade ${n} bredde mål`] = `${board.width} ${unit}`;
        }
        props[`_board_${n}_length`] = dim.length;
        props[`_board_${n}_width`] = dim.width;
      });

      // Edges
      this.state.edges.forEach((boardEdges, i) => {
        const n = i + 1;
        ['A', 'B', 'C', 'D'].forEach(side => {
          const edge = this.config.edges.find(e => e.handle === boardEdges[side]);
          if (edge && edge.handle !== 'sharp') {
            props[`Plade ${n} kant ${side}`] = edge.name;
          }
          props[`_board_${n}_edge_${side}`] = boardEdges[side];
        });
      });

      // Corners — Round 6 follow-up (7 May 2026): also store a human-readable
      // line per corner cutout (Thomas: "we don't save data for the corner
      // cutouts as far as I can see"). Matches the edge / cutout pattern that
      // already shows up in cart, checkout, order admin and email.
      const _boardCount = this.formationBoardCounts[this.state.formation] || 1;
      Object.entries(this.state.corners).forEach(([key, corner]) => {
        if (corner.type === 'square') {
          const [bn, ck] = key.split('_'); // e.g. "1_AB" → "1", "AB"
          const boardSuffix = _boardCount > 1 ? ` — Plade ${bn}` : '';
          const corner_letters = ck[0] + '-' + ck[1]; // "AB" → "A-B"
          props[`Hjørneuds. ${corner_letters}${boardSuffix}`] = `Længde ${corner.length} ${unit}, dybde ${corner.depth} ${unit}`;
          props[`_corner_${key}_type`] = 'square';
          props[`_corner_${key}_length`] = corner.length;
          props[`_corner_${key}_depth`] = corner.depth;
        }
      });

      // Cutouts — P3 #19: only store if all dimensions are present
      // P4: Client feedback — rename "Vask udskæring" → "Vaskudskæring + underlimning",
      // include target board for multi-board formations, store board_index.
      const boardCount = this.formationBoardCounts[this.state.formation];
      ['sink', 'hob'].forEach(type => {
        const cutout = this.state.cutouts[type];
        if (cutout.enabled && cutout.width && cutout.depth && cutout.center_x) {
          const boardIdx = cutout.board_index || 1;
          const boardSuffix = boardCount > 1 ? ` — Plade ${boardIdx}` : '';
          // Round 7 (7 May 2026): renamed sink label "Vask + underlimning"; 65mm-fra-A
          // is a global constant (stays in the step-6 UI label) so we don't repeat it
          // in every cart line item — keeps the line readable.
          const label = type === 'sink' ? 'Vask + underlimning' : 'Kogeplade udskæring';
          // Round 6 follow-up (7 May 2026): include center_x in the visible text
          // so production sees the position along side B, not just the dimensions.
          // Front-edge offset (6 cm from side A) is a fixed config value and stays
          // implicit in the drawing.
          props[`${label}${boardSuffix}`] = `${cutout.width} × ${cutout.depth} ${unit}, midte ${cutout.center_x} ${unit} fra side B`;
          props[`_cutout_${type}`] = 'yes';
          props[`_cutout_${type}_width`] = cutout.width;
          props[`_cutout_${type}_depth`] = cutout.depth;
          props[`_cutout_${type}_center_x`] = cutout.center_x;
          props[`_cutout_${type}_board_index`] = boardIdx;
        }
      });

      // P5 #4 (23 April): record the chosen sink product on the board's line item
      // (the sink itself is also added to the cart as a separate line item by addToCart).
      const sink = this.state.cutouts.sink;
      if (sink.enabled && sink.product_handle) {
        props['Valgt vask'] = sink.product_title;
        props['_sink_product_handle'] = sink.product_handle;
        props['_sink_variant_id'] = sink.variant_id;
      }

      // Surface — P4: treatments concept removed (always implicit 1 pass)
      if (this.state.surface !== 'none') {
        props['Overfladebehandling'] = surf?.name || '';
        props['_surface'] = this.state.surface;
      }

      // Phase 8: name the chosen package on the board line too. It is already its own cart
      // line, but production reads the board's properties, and Thomas wants to see at a
      // glance whether a technician visit was bought with the board.
      const pkgs = this.getSelectedPackages();
      if (pkgs.length) {
        props['Servicepakke'] = pkgs.map(p => p.title).join(', ');
        props['_service_package'] = pkgs.map(p => p.handle).join(',');
      }

      // JSON-serialized data for server-side price recalculation
      const boardsMM = this.state.boards.map(b => this.getDimensionMM(b));
      props['_boards'] = JSON.stringify(boardsMM);
      props['_edges'] = JSON.stringify(this.state.edges);
      props['_corners'] = JSON.stringify(this.state.corners);
      const cutoutData = {};
      ['sink', 'hob'].forEach(type => {
        const c = this.state.cutouts[type];
        if (c.enabled && c.width && c.depth && c.center_x) {
          // Store as object with board_index so server-side recalc can re-validate clearance
          const factor = this.state.unit === 'cm' ? 10 : 1;
          cutoutData[type] = {
            enabled: true,
            board_index: c.board_index || 1,
            width_mm: this.parseDimension(c.width) * factor,
            depth_mm: this.parseDimension(c.depth) * factor,
            center_x_mm: this.parseDimension(c.center_x) * factor
          };
        }
      });
      props['_cutouts'] = JSON.stringify(cutoutData);

      // Price summary — board line ONLY. Both the sink and, since phase 8, the service
      // package are separate cart lines that Shopify prices itself, so both are subtracted
      // here for exactly the same reason they are subtracted from `_calculated_price`.
      // Leaving the package in made the board line read "Pris inkl. moms: 13.295,15" while
      // being charged 6.300,15, with the difference already billed on its own line.
      const separateLinesExVat = (prices.sink || 0) + (prices.service_package || 0);
      const subtotalBoardOnly = prices.subtotal - separateLinesExVat;
      const totalBoardOnly = subtotalBoardOnly * (1 + (this.config.vat_rate || BoardCalculator.VAT_RATE_DEFAULT));
      props['Pris ekskl. moms'] = this.formatDKK(subtotalBoardOnly);
      props['Pris inkl. moms'] = this.formatDKK(totalBoardOnly);

      // Drawing is attached separately by addToCart so the upload to Shopify Files
      // can run async without delaying buildLineItemProperties.

      return props;
    }

    /** POST a PNG data URL to the Netlify upload-drawing endpoint, which uploads
     *  to Shopify Files and returns a permanent CDN URL. Returns null on any
     *  failure so the caller can fall back to the inline SVG. */
    async uploadDrawing(pngDataUrl) {
      if (!pngDataUrl || !pngDataUrl.startsWith('data:image/png;base64,')) return null;
      const filename = `drawing-${Date.now()}-${Math.floor(Math.random() * 10000)}.png`;
      try {
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        // Generous timeout: stagedUploads + fileCreate + poll can take ~5s on
        // Shopify's side. Cap at 8s so we don't strand the user on a stalled cart.
        const timer = ctrl ? setTimeout(() => ctrl.abort(), 8000) : null;
        const res = await fetch('https://mifa-backend.netlify.app/.netlify/functions/upload-drawing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: pngDataUrl, filename, alt: this.getFormationLabel() + ' drawing' }),
          signal: ctrl?.signal
        });
        if (timer) clearTimeout(timer);
        if (!res.ok) {
          this.log('uploadDrawing non-2xx', res.status);
          return null;
        }
        const data = await res.json();
        return data.url || null;
      } catch (e) {
        this.log('uploadDrawing failed', e?.message || e);
        return null;
      }
    }

    /** Serialize the live preview SVG into a self-contained data URL. CSS var()
     *  references are resolved against the document so the result renders standalone. */
    buildDrawingSvgDataUrl() {
      const svgEl = this.querySelector('#calculator-svg');
      if (!svgEl) return null;
      const clone = svgEl.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      let svgData = new XMLSerializer().serializeToString(clone);
      const styles = getComputedStyle(document.documentElement);
      svgData = svgData.replace(/var\(\s*--([^,)]+)(?:\s*,\s*[^)]+)?\s*\)/g, (_, varName) => {
        return styles.getPropertyValue('--' + varName.trim()).trim() || '#000';
      });
      return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
    }

    getFormationLabel() {
      const labels = {
        single_rect: 'Enkelt rektangulær', round: 'Rund',
        l_right: 'L-form (højre)', l_left: 'L-form (venstre)',
        l_top: 'L-form (top)', l_bottom: 'L-form (bund)',
        u_sides: 'U-form (sider)', u_top_bottom: 'U-form (top/bund)'
      };
      return labels[this.state.formation] || this.state.formation;
    }

    // ─── PNG Download ─────────────────────────────────────────────

    /** PNG export — adapt canvas to SVG viewBox dimensions. Anchor is appended to DOM
     *  before .click() for cross-browser compatibility (Firefox + some Safari versions
     *  silently ignore .click() on detached anchors). */
    downloadPNG() {
      this.renderSvgToPng(BoardCalculator.PNG_SCALE).then(dataUrl => {
        if (!dataUrl) {
          console.warn('[BoardCalculator] downloadPNG: renderSvgToPng returned null');
          return;
        }
        const link = document.createElement('a');
        link.download = 'mifa-bordplade-tegning.png';
        link.href = dataUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        // Detach in next tick so the click event is handled before removal
        setTimeout(() => link.remove(), 0);
      }).catch(err => {
        console.error('[BoardCalculator] downloadPNG failed:', err);
      });
    }

    // Render the calculator SVG preview to a PNG data URL.
    // `scale` is a multiplier on the SVG viewBox dimensions (e.g. 2 for retina download, 0.6 for cart thumbnail).
    renderSvgToPng(scale) {
      return new Promise(resolve => {
        const svgEl = this.querySelector('#calculator-svg');
        if (!svgEl) {
          console.warn('[BoardCalculator] renderSvgToPng: #calculator-svg not found');
          return resolve(null);
        }

        const vb = svgEl.getAttribute('viewBox')?.split(' ').map(Number) || [0, 0, 500, 400];
        const vbW = vb[2] || 500;
        const vbH = vb[3] || 400;

        // Clone the live SVG and inject xmlns + computed CSS-var values so the
        // serialized payload is self-contained and parseable as a standalone image.
        const clone = svgEl.cloneNode(true);
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        let svgData = new XMLSerializer().serializeToString(clone);
        const styles = getComputedStyle(document.documentElement);
        // Resolve var(--x) AND var(--x, fallback) — the second form was previously left
        // intact and broke parsing in some renderers.
        svgData = svgData.replace(/var\(\s*--([^,)]+)(?:\s*,\s*[^)]+)?\s*\)/g, (_, varName) => {
          return styles.getPropertyValue('--' + varName.trim()).trim() || '#000';
        });

        const canvas = document.createElement('canvas');
        canvas.width = vbW * scale;
        canvas.height = vbH * scale;
        const ctx = canvas.getContext('2d');

        const img = new Image();
        img.onload = () => {
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          try {
            resolve(canvas.toDataURL('image/png'));
          } catch (e) {
            // Tainted-canvas guard
            console.error('[BoardCalculator] canvas.toDataURL failed (likely cross-origin):', e);
            resolve(null);
          }
        };
        img.onerror = (e) => {
          console.error('[BoardCalculator] SVG → image load failed', e);
          resolve(null);
        };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
      });
    }
  });
}
